import React, { useState, useEffect, useMemo, useRef } from "react";
import { FaInfoCircle, FaExclamationTriangle } from "react-icons/fa";
import { FiChevronLeft, FiEdit, FiSave, FiTrash2, FiArrowRight } from "react-icons/fi";
import { BiChevronDown } from "react-icons/bi";
import {
  Accordion,
  AccordionItem,
  AccordionItemHeading,
  AccordionItemButton,
  AccordionItemPanel,
} from "react-accessible-accordion";
import { Prompt } from "react-router-dom";
import PickDate from "../../components/common/datePicker";
import InputDatePicker from "../../components/common/inputDatePicker";
import FooterButton from "../../components/footerButton";
import { getScore } from "../../services/plan_service";
import {
  getScoreOptionPillProps,
  scaleResolverFor,
} from "../../utils/scoringScale";
import GetInterventions from "../../hooks/getInterventions";
import GetMeetingTypes from "../../hooks/getMeetingTypes";
import GetPositions from "../../hooks/getPositions";
import GetUsers from "../../hooks/getUsers";
import GetPatientCareProviders from "../../hooks/getPatientCareProviders";
import AddCarePlan from "../../hooks/createCarePlan";
import UpdateCarePlan from "../../hooks/updateCarePlan";
import AmendCarePlan from "../../hooks/amendCarePlan";
import GetCarePlanById from "../../hooks/getCarePlanById";
import useCarePlanInitialScore from "../../hooks/getCarePlanInitialScore";
import useCarePlanCurrentScore from "../../hooks/getCarePlanCurrentScore";
import GetFunctionalSummary from "../../hooks/getFunctionalSummary";
import LatestFunctionalScoresOverlay from "../../components/hcp/LatestFunctionalScoresOverlay";
import TeamScoresOverlay from "../../components/hcp/TeamScoresOverlay";
import { useQueryClient } from "@tanstack/react-query";
import GetFunctionalChecklist from "../../hooks/getFunctionalChecklist";
import { Bounce, toast } from "react-toastify";
import "react-toastify/ReactToastify.css";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import moment from "moment";
import {
  getRemovedBaselineSubheadings,
  addRemovedBaselineSubheading,
  restoreRemovedBaselineSubheading,
  clearRemovedBaselineSubheadings,
} from "../../utils/removedBaselineSubheadings";

// Normalize a score so colored pills (which compare with === against numbers)
// match: numeric strings become numbers, "N/A" stays a string, null stays null.
const parseScore = (val) => {
  if (val == null) return null;
  const n = Number(val);
  return Number.isNaN(n) ? val : n;
};

// Transform outstanding care plan data to match the expected format
const transformOutstandingCarePlan = (carePlanData) => {
  if (!carePlanData || !carePlanData.headings) return [];

  // Safe date parsing function
  const parseSafeDate = (dateString) => {
    if (!dateString) return null;
    try {
      const date = new Date(dateString);
      return isNaN(date.getTime()) ? null : date;
    } catch (error) {
      console.error("Error parsing date:", dateString, error);
      return null;
    }
  };

  // Transform headings and their functional issues
  return carePlanData.headings.map((heading) => {
    // Filter functional issues that have actual data (not null values)
    const functionalIssuesWithData = heading.functionalIssues.filter(
      (issue) => issue.id !== null || issue.currentScore !== null
    );

    // If no functional issues have data, create one empty functional issue like care_management does
    const functional_issues =
      functionalIssuesWithData.length > 0
        ? functionalIssuesWithData
          .slice()
          .sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0))
          .map((issue) => ({
            id: issue.id || `temp-${issue.subheadingId}`,
            subheading: {
              id: issue.subheadingId,
              name: issue.subheadingName,
            },
            current_score: parseScore(issue.currentScore),
            target_score: parseScore(issue.targetScore),
            current_score_date: parseSafeDate(issue.assignedDate),
            comment: issue.comment || "",
            orderIndex: issue.orderIndex ?? 0,
            isEdit: false,
            intervention:
              issue.interventions && issue.interventions.length > 0
                ? issue.interventions
                    .slice()
                    .sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0))
                    .map((intervention) => ({
                      id: intervention.labelId,
                      name: intervention.labelName,
                      position: intervention.position,
                      provider: intervention.assignedCareProvider,
                      review_date: parseSafeDate(intervention.reviewDate),
                      comment: intervention.comment || "",
                      orderIndex: intervention.orderIndex ?? 0,
                      isEdit: false,
                    }))
                : [],
          }))
        : [{ id: 1, intervention: [{}], isEdit: false }]; // Default empty functional issue like care_management

    return {
      id: heading.id,
      heading: {
        name: heading.name,
        subheadings: [], // Not used in this context
      },
      functional_issues,
    };
  }); // Include all headings, even if empty
};

const ICON_SIZE = 20;

const Item1 = ({ onClear }) => (
  <button className="btn btn_danger px-5" onClick={onClear}>
    <small>CLEAR</small>
  </button>
);

const Item2 = ({
  props,
  date,
  handleSaveForLater,
  handleSign,
  carePlan,
  type,
  meeting,
  meetingTypeId,
  attendees,
  onAllowNavigation,
  carePlanId,
  editReason,
  carePlanStatus,
  carePlanVersion,
  fromTeamScores,
  teamScoreFromDate,
  teamScoreToDate,
}) => {
  const hasAtLeastOneFunctionalIssue = carePlan?.some((heading) =>
    heading.functional_issues?.some((issue) => issue.subheading)
  );

  const handleBack = () => {
    onAllowNavigation();

    // Always go back to care_management with current data (works for both fresh and outstanding flows)
    const stateToPass =
      carePlan && carePlan.length > 0
        ? {
            date,
            carePlan,
            type,
            meeting,
            meetingTypeId,
            attendees,
            fromReview: true,
            carePlanId,
            editReason,
            carePlanStatus,
            carePlanVersion,
            fromTeamScores,
            fromDate: teamScoreFromDate,
            toDate: teamScoreToDate,
          }
        : {
            date,
            carePlanId,
            editReason,
            carePlanStatus,
            carePlanVersion,
            fromTeamScores,
            fromDate: teamScoreFromDate,
            toDate: teamScoreToDate,
          };

    setTimeout(() => {
      props.history.push({
        pathname: "/hcp/patient/care_management",
        state: stateToPass,
      });
    }, 0);
  };

  const handleSaveWithValidation = () => {
    if (!hasAtLeastOneFunctionalIssue) {
      toast.warn("Please select a functional issue", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "light",
        transition: Bounce,
      });
      return;
    }
    handleSaveForLater();
  };

  const handleSignWithValidation = () => {
    if (!hasAtLeastOneFunctionalIssue) {
      toast.warn("Please select a functional issue", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "light",
        transition: Bounce,
      });
      return;
    }
    handleSign();
  };

  return (
    <div>
      <button onClick={handleBack} className="btn btn_outline_light px-3 mx-3">
        <FiChevronLeft size={ICON_SIZE} />
        <small>BACK TO CARE PLAN</small>
      </button>
      <button
        onClick={handleSaveWithValidation}
        className="btn btn_outline_light px-3 mx-3"
      >
        <small>SAVE FOR LATER</small>
      </button>
      <button onClick={handleSignWithValidation} className="btn btn_light px-5 pr-2 ml-3">
        <small>SIGN</small>
      </button>
    </div>
  );
};

const ReviewCarePlan = (props) => {
  const locationState = props.location.state || {};

  // Check localStorage for outstanding care plan data
  const outstandingCarePlanId = localStorage.getItem("outstandingCarePlanId");
  const outstandingCarePlanDate = localStorage.getItem(
    "outstandingCarePlanDate"
  );
  const isFromOutstanding = localStorage.getItem("fromOutstanding") === "true";

  const {
    date,
    carePlan: initialCarePlan,
    type,
    meeting,
    meetingTypeId,
    attendees,
    carePlanId,
    fromOutstanding,
    editReason: editReasonFromState,
    carePlanStatus: statusFromState,
    carePlanVersion: versionFromState,
    fromTeamScores,
    fromDate: teamScoreFromDate,
    toDate: teamScoreToDate,
  } = locationState;


  // Safe date parsing function
  const parseSafeDate = (dateString) => {
    if (!dateString) return null;
    try {
      const date = new Date(dateString);
      return isNaN(date.getTime()) ? null : date;
    } catch (error) {
      console.error("Error parsing date:", dateString, error);
      return null;
    }
  };

  // Use localStorage data if available, otherwise use location state
  const finalCarePlanId = carePlanId || outstandingCarePlanId;
  const finalFromOutstanding = fromOutstanding || isFromOutstanding;
  const finalDate = date || parseSafeDate(outstandingCarePlanDate);

  // State management
  const [carePlan, setCarePlan] = useState(initialCarePlan || []);
  const [providers, setProviders] = useState([]);
  const [outstandingCarePlanData, setOutstandingCarePlanData] = useState(null);
  const [carePlanType, setCarePlanType] = useState(type);
  const [carePlanMeeting, setCarePlanMeeting] = useState(meeting);
  // `carePlanMeeting` holds the meeting-type name for display; the numeric
  // `carePlanMeetingTypeId` is what the create/update payload sends.
  const [carePlanMeetingTypeId, setCarePlanMeetingTypeId] = useState(
    meetingTypeId ?? null,
  );
  const [carePlanAttendees, setCarePlanAttendees] = useState(attendees);
  const [carePlanDate, setCarePlanDate] = useState(finalDate);
  // Versioning: when editing a SIGNED care plan we must amend (create a new
  // version) rather than edit in place. The reason is captured here and only
  // sent to the backend when the user actually saves/signs.
  const [carePlanStatus, setCarePlanStatus] = useState(statusFromState || null);
  const [carePlanVersion, setCarePlanVersion] = useState(
    versionFromState || null,
  );
  const [editReason, setEditReason] = useState(editReasonFromState || "");
  // The care-plan body (type, headings) is revealed only after the user
  // submits the reason via the arrow button. A reason round-tripped from
  // navigation counts as already confirmed.
  const [editReasonConfirmed, setEditReasonConfirmed] = useState(
    Boolean(editReasonFromState && editReasonFromState.trim()),
  );
  const isAmendMode = carePlanStatus === "SIGNED";
  // A reason is required — and the care-plan type/headings stay hidden until it
  // is entered and submitted — when amending a signed plan or editing any
  // version > 1.
  const requiresEditReason = isAmendMode || carePlanVersion > 1;
  const showCarePlanBody = !requiresEditReason || editReasonConfirmed;
  // Autopopulation (auto-adding baseline subheading rows and auto-filling
  // current scores) only helps when filling a brand-new care plan. A care plan
  // that was already saved (outstanding draft, or signed/amend) has an id, so
  // we render only its saved issues and never auto-add or auto-fill current.
  const isFreshCarePlan = !finalCarePlanId;
  const teamScoreRange = useMemo(() => {
    // Prefer the dates carried over from the team-scores flow. When they are
    // absent (e.g. opening an existing care plan from Care Plan Summaries and
    // editing it) anchor on the care plan's own date so the team scores shown
    // are the ones for that plan's period, not simply the last 7 days.
    const anchorEnd =
      carePlanDate && moment(carePlanDate).isValid()
        ? moment(carePlanDate).startOf("day")
        : moment().startOf("day");
    const rangeEnd =
      teamScoreToDate && moment(teamScoreToDate).isValid()
        ? moment(teamScoreToDate).startOf("day")
        : anchorEnd;
    const rangeStart =
      teamScoreFromDate && moment(teamScoreFromDate).isValid()
        ? moment(teamScoreFromDate).startOf("day")
        : moment(rangeEnd).subtract(7, "days").startOf("day");
    return {
      start: rangeStart,
      end: rangeEnd,
      startDate: rangeStart.format("YYYY-MM-DDTHH:mm:ss.SSS"),
      endDate: rangeEnd.format("YYYY-MM-DDTHH:mm:ss.SSS"),
    };
  }, [teamScoreFromDate, teamScoreToDate, carePlanDate]);
  const [actionType, setActionType] = useState(null); // Track whether user clicked SIGN or SAVE_FOR_LATER
  const [showClearModal, setShowClearModal] = useState(false); // Clear button confirmation state
  const [showAcceptChangesModal, setShowAcceptChangesModal] = useState(false);
  const [acceptChangesAction, setAcceptChangesAction] = useState(null);
  const [showClearRowModal, setShowClearRowModal] = useState(false);
  const [clearRowAction, setClearRowAction] = useState(null);
  const [isEditingMeetingType, setIsEditingMeetingType] = useState(false);
  const [isEditingAttendees, setIsEditingAttendees] = useState(false);
  const [showAttendeesList, setShowAttendeesList] = useState(false);
  const [openDropdownKey, setOpenDropdownKey] = useState(null);

  // Navigation confirmation states
  const [showNavigationModal, setShowNavigationModal] = useState(false);
  const [pendingLocation, setPendingLocation] = useState(null);
  const [allowNavigation, setAllowNavigation] = useState(false);
  const [hasDataBeenEntered, setHasDataBeenEntered] = useState(false);
  const [showLatestScoresOverlay, setShowLatestScoresOverlay] = useState(false);
  const [showTeamScoreOverlay, setShowTeamScoreOverlay] = useState(false);

  // Data hooks
  const {
    data: interventions,
    isLoading: isLoadingInterv,
    error: errorInterv,
  } = GetInterventions();
  const { data: meetingTypesData } = GetMeetingTypes();
  const {
    data: positions,
    isLoading: isLoadingPositions,
    error: errorPositions,
  } = GetPositions();
  const {
    data: organizationHcps,
    isLoading: isLoadingProviders,
    error: errorProviders,
  } = GetUsers();
  // Attendees are the patient's own collaborators, not everyone in the
  // organization. `organizationHcps` above still backs the intervention
  // assignee dropdown, which is a different question.
  const { data: patientCareProviders } = GetPatientCareProviders();
  const {
    data: functionalChecklistData,
    isLoading: isLoadingFunctionalChecklist,
    error: errorFunctionalChecklist,
  } = GetFunctionalChecklist();

  // The scale each row is scored on: the care-plan payload carries the
  // subheading but not the range it was scored on, so it is resolved from the
  // checklist heading the subheading belongs to.
  const resolveScale = scaleResolverFor(functionalChecklistData?.data?.[0]?.headings || []);

  const patientId = localStorage.getItem("patient");
  // The logged-in care provider — the author of anything created here.
  const currentProviderId = useMemo(() => {
    try {
      return (
        JSON.parse(localStorage.getItem("user"))?.organizations?.[0]
          ?.careProvider?.id ?? null
      );
    } catch {
      return null;
    }
  }, []);
  // The patient's collaborators, minus the logged-in provider — they are the
  // author of this care plan, so they are added on save rather than picked.
  const attendeeOptions = useMemo(
    () =>
      (patientCareProviders?.data || []).filter(
        (provider) => provider?.id != null && provider.id !== currentProviderId,
      ),
    [patientCareProviders, currentProviderId],
  );
  const queryClient = useQueryClient();
  // Initial and current scores come from two separate endpoints. For
  // INDIVIDUAL both are scoped to the logged-in provider's own assessments.
  const { data: initialScoreData } = useCarePlanInitialScore(patientId, carePlanType?.value);
  const { data: currentScoreData } = useCarePlanCurrentScore(patientId, carePlanType?.value);

  // Past-week range for the Functional Scores overlay (last 7 days).
  const functionalScoresWeekRange = useMemo(() => {
    const end = moment().endOf("day");
    const start = moment().subtract(6, "days").startOf("day");
    return {
      startDate: start.format("YYYY-MM-DDTHH:mm:ss.SSS"),
      endDate: end.format("YYYY-MM-DDTHH:mm:ss.SSS"),
    };
  }, []);
  const functionalScoresOverlayRange = fromTeamScores
    ? {
        startDate: teamScoreRange.startDate,
        endDate: teamScoreRange.endDate,
      }
    : functionalScoresWeekRange;

  // Functional Summaries tab data: all scores for the past week, final
  // (current) versions only — no historical versions.
  const {
    data: functionalScoresData,
    isLoading: isLatestScoresLoading,
    error: latestScoresError,
  } = GetFunctionalSummary(
    patientId,
    functionalScoresOverlayRange.startDate,
    functionalScoresOverlayRange.endDate,
    null,
    null,
    null,
    null,
    null,
    false, // includeTeamScores
    false, // includeHistory — only final versions
  );

  const normalizeBaselineEntry = (entry) => {
    if (!entry) return null;
    return {
      initial_score: entry.initialScore != null ? (Number(entry.initialScore) || entry.initialScore) : undefined,
      initial_score_date: entry.initialAssessmentDate ? new Date(entry.initialAssessmentDate) : undefined,
      current_score: entry.currentScore != null ? (Number(entry.currentScore) || entry.currentScore) : undefined,
      current_score_date: entry.currentAssessmentDate ? new Date(entry.currentAssessmentDate) : undefined,
    };
  };
  // Merge the two endpoints back into the one entry-per-subheading shape the
  // rest of the page consumes. A subheading with no answer is absent from
  // either map, so an entry may carry an initial without a current or vice
  // versa — unlike the deprecated combined endpoint, neither stands in for the
  // other.
  const baselineScores = useMemo(() => {
    const initial = initialScoreData?.data || {};
    const current = currentScoreData?.data || {};
    const subheadingIds = new Set([
      ...Object.keys(initial),
      ...Object.keys(current),
    ]);
    if (subheadingIds.size === 0) return null;
    // Collect all subheading IDs from the current checklist. If none of them
    // carry a score this is a fresh careplan for this checklist — hide the
    // current score.
    const checklistSubheadingIds = new Set(
      (functionalChecklistData?.data || []).flatMap((checklist) =>
        (checklist.headings || []).flatMap((heading) =>
          (heading.subheadings || []).map((sub) => sub.id)
        )
      )
    );
    const merged = {};
    subheadingIds.forEach((id) => {
      if (!checklistSubheadingIds.has(id)) return;
      merged[id] = {
        initialScore: initial[id]?.score ?? null,
        initialAssessmentDate: initial[id]?.assessmentDate ?? null,
        currentScore: current[id]?.score ?? null,
        currentAssessmentDate: current[id]?.assessmentDate ?? null,
      };
    });
    return Object.keys(merged).length > 0 ? merged : null;
  }, [initialScoreData, currentScoreData, functionalChecklistData]);

  const {
    data: teamScoreData,
    isLoading: isTeamScoreLoading,
    error: teamScoreError,
  } = GetFunctionalSummary(
    patientId,
    teamScoreRange.startDate,
    teamScoreRange.endDate,
    null,
    null,
    null,
    null,
    null,
    true,
  );
  const medianScores = useMemo(() => {
    if (carePlanType?.value !== "COLLABORATIVE") return {};
    const result = {};
    (teamScoreData?.data || []).forEach((heading) => {
      (heading.subheadings || []).forEach((sub) => {
        if (sub.id && sub.median > 0) {
          result[sub.id] = Math.floor(sub.median);
        }
      });
    });
    return result;
  }, [teamScoreData, carePlanType?.value]);

  const functionalScoresWithTeamScores = useMemo(() => {
    const teamScoresBySubheadingId = new Map();
    (teamScoreData?.data || []).forEach((heading) => {
      (heading.subheadings || []).forEach((subheading) => {
        if (subheading?.id) {
          teamScoresBySubheadingId.set(subheading.id, {
            median: subheading.median,
            teamScore: subheading.teamScore,
          });
        }
      });
    });

    return {
      ...(functionalScoresData || {}),
      data: (functionalScoresData?.data || []).map((heading) => ({
        ...heading,
        subheadings: (heading.subheadings || []).map((subheading) => ({
          ...subheading,
          ...(teamScoresBySubheadingId.get(subheading.id) || {}),
        })),
      })),
    };
  }, [functionalScoresData, teamScoreData]);

  // Care plan mutation hooks
  const {
    mutate,
    isLoading: createLoading,
    isSuccess,
    error: createError,
  } = AddCarePlan();

  const {
    mutate: updateMutate,
    isLoading: updateLoading,
    isSuccess: updateSuccess,
    error: updateError,
  } = UpdateCarePlan();

  const { mutate: amendMutate, isLoading: amendLoading } = AmendCarePlan();

  // Hook to fetch care plan by ID when coming from outstanding documents
  const carePlanIdToFetch = finalFromOutstanding ? finalCarePlanId : null;

  const {
    data: outstandingCarePlan,
    isLoading: isLoadingOutstanding,
    error: errorOutstanding,
  } = GetCarePlanById(carePlanIdToFetch);

  // Handle outstanding care plan data
  useEffect(() => {
    if (finalFromOutstanding && outstandingCarePlan && !isLoadingOutstanding) {
      // Transform outstanding care plan data to match the expected format
      const transformedData = transformOutstandingCarePlan(
        outstandingCarePlan.data
      );
      setOutstandingCarePlanData(transformedData);

      // Set care plan metadata from outstanding document
      const carePlanData = outstandingCarePlan.data;
      // Capture status — a SIGNED plan must be amended (new version), not edited
      setCarePlanStatus(carePlanData?.status || null);
      setCarePlanVersion(carePlanData?.versionNumber ?? null);
      // Restore the stored reason only when reopening an in-progress amendment
      // draft (a DRAFT with a previous version). Originals never carry a reason,
      // and a fresh amend (source still SIGNED) must capture a new one, so both
      // start empty. The `!editReason.trim()` guard keeps a value the user just
      // typed — round-tripped via navigation state — from being clobbered by a
      // refetch.
      if (
        carePlanData?.status === "DRAFT" &&
        carePlanData?.previousVersionId != null &&
        carePlanData?.editReason?.trim() &&
        !editReason.trim()
      ) {
        setEditReason(carePlanData.editReason);
        setEditReasonConfirmed(true);
      }
      setCarePlanType({
        value: carePlanData?.type,
        label:
          carePlanData?.type === "INDIVIDUAL"
            ? "Individual Provider Care Plan"
            : "Collaborative Care Plan",
      });

      // Set meeting type for collaborative care plans
      if (carePlanData?.type === "COLLABORATIVE") {
        if (carePlanData?.typeOfMeeting) {
          setCarePlanMeeting(carePlanData?.typeOfMeeting);
        }
        if (carePlanData?.meetingTypeId != null) {
          setCarePlanMeetingTypeId(carePlanData.meetingTypeId);
        }

        // Set attendees for collaborative care plans
        // Check if attendees data exists in the API response
        if (carePlanData?.attendees && carePlanData?.attendees?.length > 0) {
          setCarePlanAttendees({
            confirmedAttendees: true,
            attendees: carePlanData.attendees.map(
              (attendee) => attendee.id || attendee
            ),
          });
        } else {
          // If no attendees in API response, create a default structure for collaborative plans
          // This ensures the UI shows attendees section even if empty
          setCarePlanAttendees({
            confirmedAttendees: true,
            attendees: [], // Empty but confirmed to show the section
          });
        }
      }

      // Set the date from the care plan data if not already set
      if (!carePlanDate && carePlanData.entryDate) {
        const parsedDate = parseSafeDate(carePlanData.entryDate);
        if (parsedDate) {
          setCarePlanDate(parsedDate);
        }
      }

      // Note: The API response doesn't include attendees data in this structure
      // If attendees are needed, they would need to be fetched separately or
      // included in the API response
    }
  }, [finalFromOutstanding, outstandingCarePlan, isLoadingOutstanding]);

  // Initialize care plan with edit states
  useEffect(() => {
    if (initialCarePlan && initialCarePlan.length > 0) {
      const carePlanWithEditState = initialCarePlan.map((heading) => ({
        ...heading,
        functional_issues: heading.functional_issues.map((issue) => ({
          ...issue,
          isEdit: false,
          intervention: issue.intervention.map((interv) => ({
            ...interv,
            isEdit: false,
          })),
        })),
      }));
      setCarePlan(carePlanWithEditState);
    } else if (outstandingCarePlanData) {
      // Use outstanding care plan data if available
      setCarePlan(outstandingCarePlanData);
    }
  }, [initialCarePlan, outstandingCarePlanData]);

  // Prefill scores and auto-add baseline subheadings whenever baselines become available
  useEffect(() => {
    if (!baselineScores) return;
    const isIndividual = carePlanType?.value === "INDIVIDUAL";
    // For a collaborative plan created via the nav bar (not from team scores)
    // the current score must come from team scoring, so never auto-fill it —
    // mirrors the guard in care_management.jsx.
    const skipCurrentScore =
      !fromTeamScores && carePlanType?.value === "COLLABORATIVE";
    // Auto-adding baseline rows applies to the team-scores flow and to a fresh
    // Individual plan; collaborative-from-navbar is excluded so it stays blank.
    const autoAddAllowed = isFreshCarePlan && (fromTeamScores || isIndividual);
    // Subheadings the user deleted earlier in this flow (possibly on the care
    // management page) must not be auto-added back.
    const removedSubheadingIds = getRemovedBaselineSubheadings(patientId);
    const checklistHeadings = functionalChecklistData?.data?.[0]?.headings || [];
    setCarePlan((prev) => {
      if (!prev || prev.length === 0) return prev;
      let changed = false;
      const updated = prev.map((careheading) => {
        // Update scores on already-selected issues
        const updatedIssues = careheading.functional_issues.map((issue) => {
          if (!issue.subheading?.id) return issue;
          const normalized = normalizeBaselineEntry(baselineScores[issue.subheading.id]);
          if (!normalized) return issue;
          const updates = {};
          if (normalized.initial_score != null && issue.initial_score !== normalized.initial_score) {
            updates.initial_score = normalized.initial_score;
            updates.initial_score_date = normalized.initial_score_date;
          }
          if (
            isFreshCarePlan &&
            !skipCurrentScore &&
            normalized.current_score != null &&
            !issue.current_score
          ) {
            updates.current_score = normalized.current_score;
            updates.current_score_date = normalized.current_score_date;
          }
          if (Object.keys(updates).length > 0) {
            changed = true;
            return { ...issue, ...updates };
          }
          return issue;
        });
        // Auto-add rows for baseline subheadings not already present
        const checklistHeading = checklistHeadings.find((h) => h.id === careheading.id);
        const availableSubheadings = checklistHeading?.subheadings || [];
        const existingSubheadingIds = new Set(
          updatedIssues.filter((fi) => fi.subheading?.id).map((fi) => fi.subheading.id)
        );
        // Only auto-add baseline rows for a fresh care plan (and never for
        // collaborative-from-navbar); a saved plan must show only saved issues.
        const subheadingsToAdd = autoAddAllowed
          ? availableSubheadings.filter(
              (sub) =>
                baselineScores[sub.id] &&
                !existingSubheadingIds.has(sub.id) &&
                !removedSubheadingIds.has(sub.id)
            )
          : [];
        if (subheadingsToAdd.length > 0) {
          changed = true;
          const maxId = updatedIssues.reduce(
            (max, fi) => Math.max(max, typeof fi.id === "number" ? fi.id : 0), 0
          );
          const newIssues = subheadingsToAdd.map((sub, idx) => {
            const normalized = normalizeBaselineEntry(baselineScores[sub.id]);
            return {
              id: maxId + idx + 1,
              subheading: sub,
              intervention: [],
              isEdit: false,
              ...(normalized.initial_score != null ? { initial_score: normalized.initial_score } : {}),
              ...(normalized.initial_score_date ? { initial_score_date: normalized.initial_score_date } : {}),
              ...(!skipCurrentScore && normalized.current_score != null ? { current_score: normalized.current_score } : {}),
              ...(!skipCurrentScore && normalized.current_score_date ? { current_score_date: normalized.current_score_date } : {}),
            };
          });
          const nonEmptyIssues = updatedIssues.filter((fi) => fi.subheading?.id);
          return { ...careheading, functional_issues: [...nonEmptyIssues, ...newIssues] };
        }
        return changed
          ? { ...careheading, functional_issues: updatedIssues }
          : careheading;
      });
      return changed ? updated : prev;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baselineScores, carePlanType?.value]);

  // Handle success messages
  useEffect(() => {
    if (isSuccess || updateSuccess) {
      queryClient.invalidateQueries({ queryKey: ["carePlanInitialScores", patientId] });
      queryClient.invalidateQueries({ queryKey: ["carePlanCurrentScores", patientId] });
      // Clear sessionStorage flag since data is successfully saved
      sessionStorage.removeItem("hasUnsavedCarePlan");
      // The plan is saved, so its deletions are now part of the saved issues.
      clearRemovedBaselineSubheadings(patientId);

      if (actionType === "SIGN") {
        // Custom toast for SIGN action
        toast.success(
          <p className="text-center">
            Care Plan is saved in VIEW Care Plan Summaries
          </p>,
          {
            position: "top-right",
            autoClose: false,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            style: { backgroundColor: "#353C51" },
            theme: "colored",
            transition: Bounce,
          }
        );
      } else {
        // Default toast for SAVE_FOR_LATER action
        toast.success(
          <p className="text-center">
            Your unfinished document <br /> will be saved in
            <br /> OUTSTANDING DOCUMENTS.
          </p>,
          {
            position: "top-right",
            autoClose: false,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            className: "bg_secondary",
            theme: "colored",
            transition: Bounce,
          }
        );
      }

      // Clear localStorage when successfully saved
      localStorage.removeItem("outstandingCarePlanId");
      localStorage.removeItem("outstandingCarePlanDate");
      localStorage.removeItem("fromOutstanding");

      // Navigate based on action type
      setTimeout(() => {
        if (actionType === "SIGN") {
          // Navigate to care plan summaries page
          props.history.push({
            pathname: "/hcp/patient/plan_care_summaries",
            state: { preventBrowserBack: true },
          });
        } else if (actionType === "SAVE_FOR_LATER") {
          // Navigate to outstanding documents page
          props.history.push({
            pathname: "/hcp/outstanding_documents",
            state: { preventBrowserBack: true },
          });
        } else {
          // Default navigation (fallback)
          props.history.push({
            pathname: "/hcp/patient/list",
            state: { preventBrowserBack: true },
          });
        }
      }, 3000); // Wait 2 seconds to allow user to see the toast
    }
  }, [isSuccess, updateSuccess, actionType, props.history]);

  // Cleanup localStorage on component unmount
  useEffect(() => {
    return () => {
      // Only clear if we're not navigating back to care management
      if (!props.location.pathname.includes("care_management")) {
        localStorage.removeItem("outstandingCarePlanId");
        localStorage.removeItem("outstandingCarePlanDate");
        localStorage.removeItem("fromOutstanding");
      }
      // Clear sessionStorage flag on unmount if navigation was allowed
      if (allowNavigation) {
        sessionStorage.removeItem("hasUnsavedCarePlan");
      }
    };
  }, [allowNavigation]);

  // Track when data has been entered
  useEffect(() => {
    if (carePlan && carePlan.length > 0) {
      setHasDataBeenEntered(true);
      // Set sessionStorage flag to trigger logout warning
      sessionStorage.setItem("hasUnsavedCarePlan", "true");
    }
  }, [carePlan]);

  // Close open dropdowns on outside click
  useEffect(() => {
    const handleDocClick = () => setOpenDropdownKey(null);
    document.addEventListener("click", handleDocClick);
    return () => document.removeEventListener("click", handleDocClick);
  }, []);

  // Handle browser close/refresh warning
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasDataBeenEntered && !allowNavigation) {
        e.preventDefault();
        e.returnValue = ""; // Chrome requires returnValue to be set
        return ""; // Some browsers show this message
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasDataBeenEntered, allowNavigation]);

  // Navigation blocking function
  const handleNavigationAttempt = (location) => {
    if (allowNavigation || !hasDataBeenEntered) {
      return true; // Allow navigation
    }

    // Don't show warning for dropdown anchor links (they just add # to URL)
    if (
      location.pathname === window.location.pathname &&
      location.hash === "#"
    ) {
      return true;
    }

    setPendingLocation(location);
    setShowNavigationModal(true);
    return false; // Block navigation
  };

  // Confirm navigation and lose data
  const handleConfirmNavigation = () => {
    setShowNavigationModal(false);
    setAllowNavigation(true);
    // Clear sessionStorage flag since user is leaving
    sessionStorage.removeItem("hasUnsavedCarePlan");

    // Navigate to the intended location
    setTimeout(() => {
      if (pendingLocation) {
        props.history.push(pendingLocation.pathname);
      }
    }, 0);
  };

  // Cancel navigation
  const handleCancelNavigation = () => {
    setShowNavigationModal(false);
    setPendingLocation(null);
  };

  // Handle functional issue selection
  const handleIssueSelection = (headingId, issueId, subheading) => {
    // Picking a previously deleted subheading again undoes the deletion.
    restoreRemovedBaselineSubheading(patientId, subheading?.id);
    const updatedCarePlan = carePlan.map((heading) => {
      if (heading.id === headingId) {
        return {
          ...heading,
          functional_issues: heading.functional_issues.map((issue) => {
            if (issue.id === issueId) {
              const normalized = normalizeBaselineEntry(baselineScores?.[subheading?.id]);
              const median = medianScores?.[subheading?.id];
              // Only auto-fill the current score for a fresh care plan, and
              // never for a collaborative plan opened from the nav bar (its
              // current score must come from team scoring) — matches
              // care_management.jsx's handleIssue.
              const blockCurrent =
                !isFreshCarePlan ||
                (!fromTeamScores && carePlanType?.value === "COLLABORATIVE");
              return {
                ...issue,
                subheading,
                ...(normalized != null ? {
                  ...(normalized.initial_score != null ? { initial_score: normalized.initial_score } : {}),
                  ...(normalized.initial_score_date ? { initial_score_date: normalized.initial_score_date } : {}),
                  ...(normalized.current_score != null && !blockCurrent ? { current_score: normalized.current_score } : {}),
                  ...(normalized.current_score_date && !blockCurrent ? { current_score_date: normalized.current_score_date } : {}),
                } : {
                  initial_score_date: new Date(),
                  ...(!blockCurrent ? { current_score_date: new Date() } : {}),
                  ...(median != null ? { initial_score: median, ...(!blockCurrent ? { current_score: median } : {}) } : {}),
                }),
              };
            }
            return issue;
          }),
        };
      }
      return heading;
    });
    setCarePlan(updatedCarePlan);
  };

  // Handle adding new functional issue
  const handleAddFunctionalIssue = (headingId) => {
    const updatedCarePlan = carePlan.map((heading) => {
      if (heading.id === headingId) {
        // Outstanding/draft rows carry non-numeric ids (backend ids or
        // `temp-…` strings). Math.max over those yields NaN, so every new row
        // would get id NaN — breaking handleIssueEdit/selection (matched by id).
        // Derive the next id from the numeric ids only to guarantee uniqueness.
        const newIssueId =
          heading.functional_issues.reduce(
            (max, issue) =>
              Math.max(max, typeof issue.id === "number" ? issue.id : 0),
            0
          ) + 1;
        return {
          ...heading,
          functional_issues: [
            ...heading.functional_issues,
            { id: newIssueId, intervention: [{}], isEdit: false },
          ],
        };
      }
      return heading;
    });
    setCarePlan(updatedCarePlan);
  };

  // Handle adding new intervention
  const handleAddIntervention = (headingId, issueId) => {
    const updatedCarePlan = carePlan.map((heading) => {
      if (heading.id === headingId) {
        return {
          ...heading,
          functional_issues: heading.functional_issues.map((issue) => {
            if (issue.id === issueId) {
              return {
                ...issue,
                intervention: [...issue.intervention, { isEdit: false }],
              };
            }
            return issue;
          }),
        };
      }
      return heading;
    });
    setCarePlan(updatedCarePlan);
  };

  // Handle edit toggle for functional issues
  const handleIssueEdit = (headingId, issueId, isEdit) => {
    const updatedCarePlan = carePlan.map((heading) => {
      if (heading.id === headingId) {
        return {
          ...heading,
          functional_issues: heading.functional_issues.map((issue) => {
            if (issue.id === issueId) {
              return { ...issue, isEdit };
            }
            return issue;
          }),
        };
      }
      return heading;
    });
    setCarePlan(updatedCarePlan);
  };

  // Handle clearing all data from functional issue
  const handleClearFunctionalIssue = (headingId, issueId) => {
    // Remember baseline-backed deletions so neither this page nor the care
    // management page auto-adds the row back on the next render.
    const cleared = carePlan
      .find((heading) => heading.id === headingId)
      ?.functional_issues.find((issue) => issue.id === issueId);
    if (cleared?.subheading?.id && baselineScores?.[cleared.subheading.id]) {
      addRemovedBaselineSubheading(patientId, cleared.subheading.id);
    }

    const updatedCarePlan = carePlan.map((heading) => {
      if (heading.id === headingId) {
        return {
          ...heading,
          functional_issues: heading.functional_issues.map((issue) => {
            if (issue.id === issueId) {
              return {
                ...issue,
                subheading: undefined,
                initial_score: undefined,
                current_score: undefined,
                target_score: undefined,
                initial_score_date: undefined,
                current_score_date: undefined,
                comment: "",
                isEdit: false,
              };
            }
            return issue;
          }),
        };
      }
      return heading;
    });
    setCarePlan(updatedCarePlan);
  };

  // Handle edit toggle for interventions
  const handleInterventionEdit = (
    headingId,
    issueId,
    interventionIndex,
    isEdit
  ) => {
    const updatedCarePlan = carePlan.map((heading) => {
      if (heading.id === headingId) {
        return {
          ...heading,
          functional_issues: heading.functional_issues.map((issue) => {
            if (issue.id === issueId) {
              return {
                ...issue,
                intervention: issue.intervention.map((interv, index) => {
                  if (index === interventionIndex) {
                    return { ...interv, isEdit };
                  }
                  return interv;
                }),
              };
            }
            return issue;
          }),
        };
      }
      return heading;
    });
    setCarePlan(updatedCarePlan);
  };

  // Handle clearing all data from intervention
  const handleClearIntervention = (headingId, issueId, interventionIndex) => {
    const updatedCarePlan = carePlan.map((heading) => {
      if (heading.id === headingId) {
        return {
          ...heading,
          functional_issues: heading.functional_issues.map((issue) => {
            if (issue.id === issueId) {
              return {
                ...issue,
                intervention: issue.intervention.map((interv, index) => {
                  if (index === interventionIndex) {
                    return { isEdit: false };
                  }
                  return interv;
                }),
              };
            }
            return issue;
          }),
        };
      }
      return heading;
    });
    setCarePlan(updatedCarePlan);
  };

  // Frozen display order per heading (heading.id -> [issueId, ...]).
  // While a row is being edited we keep the order captured before the edit
  // began so changing a score doesn't reshuffle rows mid-edit — the re-sort
  // only happens once the user commits the row via the accept-changes popup.
  const issueOrderRef = useRef({});

  const sortIssuesByScore = (issues) =>
    [...issues].sort((a, b) => {
      const sa = a.current_score,
        sb = b.current_score;
      if (sa == null || sa === "N/A") return 1;
      if (sb == null || sb === "N/A") return -1;
      return sa - sb;
    });

  // Resolve the order in which a heading's functional issues should render.
  const getDisplayIssues = (heading) => {
    const issues = heading.functional_issues || [];
    const isEditing = issues.some((issue) => issue.isEdit);

    if (!isEditing) {
      // No active edit: sort live and remember this order so an edit that
      // starts next stays frozen on it.
      const sorted = sortIssuesByScore(issues);
      issueOrderRef.current[heading.id] = sorted.map((issue) => issue.id);
      return sorted;
    }

    // A row is being edited: keep the order captured before editing began.
    const frozen = issueOrderRef.current[heading.id];
    if (!frozen) return sortIssuesByScore(issues);

    const byId = new Map(issues.map((issue) => [issue.id, issue]));
    const ordered = frozen
      .map((id) => byId.get(id))
      .filter(Boolean);
    // Append any rows not in the snapshot (e.g. newly added during the edit).
    issues.forEach((issue) => {
      if (!frozen.includes(issue.id)) ordered.push(issue);
    });
    return ordered;
  };

  // Handle score updates
  const handleScore = (score, scoreType, headingId, issueId) => {
    const updatedCarePlan = carePlan.map((heading) => {
      if (heading.id === headingId) {
        return {
          ...heading,
          functional_issues: heading.functional_issues.map((issue) => {
            if (issue.id === issueId) {
              const updates = { [scoreType]: score };
              if (scoreType === "initial_score" && !(issue.subheading && baselineScores?.[issue.subheading.id])) {
                updates.current_score = score;
              }
              return { ...issue, ...updates };
            }
            return issue;
          }),
        };
      }
      return heading;
    });
    setCarePlan(updatedCarePlan);
  };

  // Handle intervention updates
  const handleChooseIntervention = (
    interventionAction,
    intervIndex,
    headingId,
    issueId
  ) => {
    const updatedCarePlan = carePlan.map((heading) => {
      if (heading.id === headingId) {
        return {
          ...heading,
          functional_issues: heading.functional_issues.map((issue) => {
            if (issue.id === issueId) {
              const updatedInterventions = [...issue.intervention];
              updatedInterventions[intervIndex] = {
                ...updatedInterventions[intervIndex],
                ...interventionAction,
              };
              return { ...issue, intervention: updatedInterventions };
            }
            return issue;
          }),
        };
      }
      return heading;
    });
    setCarePlan(updatedCarePlan);
  };

  const handleMeetingSelection = (meetingType) => {
    setCarePlanMeeting(meetingType.name);
    setCarePlanMeetingTypeId(meetingType.id);
    setIsEditingMeetingType(false);
  };

  const handleCollaborativeAttendees = (attendee) => {
    setCarePlanAttendees((prevSelectedAttendees) => {
      const current = prevSelectedAttendees?.attendees || [];
      if (current.includes(attendee?.id)) {
        return {
          confirmedAttendees: true,
          attendees: current.filter(
            (selectedAttendee) => selectedAttendee !== attendee?.id
          ),
        };
      }
      return {
        confirmedAttendees: true,
        attendees: [...current, attendee?.id],
      };
    });
  };

  const handleConfirmedAttendees = () => {
    setCarePlanAttendees((prevState) => ({
      ...prevState,
      confirmedAttendees: true,
    }));
    setShowAttendeesList(false);
    setIsEditingAttendees(false);
  };

  const handleRemoveAttendees = () => {
    setCarePlanAttendees({
      attendees: [],
      confirmedAttendees: true,
    });
  };

  // Handle position selection
  const handlePosition = (position, interventionIndex, headingId, issueId) => {
    setProviders(
      organizationHcps?.data?.content?.filter(
        (item) => item.position.name === position.name
      ) || []
    );

    const updatedCarePlan = carePlan.map((heading) => {
      if (heading.id === headingId) {
        return {
          ...heading,
          functional_issues: heading.functional_issues.map((issue) => {
            if (issue.id === issueId) {
              const updatedInterventions = [...issue.intervention];
              updatedInterventions[interventionIndex] = {
                ...updatedInterventions[interventionIndex],
                position,
              };
              return { ...issue, intervention: updatedInterventions };
            }
            return issue;
          }),
        };
      }
      return heading;
    });
    setCarePlan(updatedCarePlan);
  };

  // Handle provider selection
  const handleProvider = (provider, interventionIndex, headingId, issueId) => {
    const updatedCarePlan = carePlan.map((heading) => {
      if (heading.id === headingId) {
        return {
          ...heading,
          functional_issues: heading.functional_issues.map((issue) => {
            if (issue.id === issueId) {
              const updatedInterventions = [...issue.intervention];
              updatedInterventions[interventionIndex] = {
                ...updatedInterventions[interventionIndex],
                provider,
              };
              return { ...issue, intervention: updatedInterventions };
            }
            return issue;
          }),
        };
      }
      return heading;
    });
    setCarePlan(updatedCarePlan);
  };

  // Handle date updates
  const handleAssignedDate = (date, dateType, headingId, issueId) => {
    const updatedCarePlan = carePlan.map((heading) => {
      if (heading.id === headingId) {
        return {
          ...heading,
          functional_issues: heading.functional_issues.map((issue) => {
            if (issue.id === issueId) {
              return { ...issue, [dateType]: date };
            }
            return issue;
          }),
        };
      }
      return heading;
    });
    setCarePlan(updatedCarePlan);
  };

  // Handle review date updates
  const handleSetReviewDate = (date, interventionIndex, headingId, issueId) => {
    const updatedCarePlan = carePlan.map((heading) => {
      if (heading.id === headingId) {
        return {
          ...heading,
          functional_issues: heading.functional_issues.map((issue) => {
            if (issue.id === issueId) {
              const updatedInterventions = [...issue.intervention];
              updatedInterventions[interventionIndex] = {
                ...updatedInterventions[interventionIndex],
                review_date: date,
              };
              return { ...issue, intervention: updatedInterventions };
            }
            return issue;
          }),
        };
      }
      return heading;
    });
    setCarePlan(updatedCarePlan);
  };

  // Handle functional issue comment updates
  const handleFunctionalIssueComment = (comment, headingId, issueId) => {
    const updatedCarePlan = carePlan.map((heading) => {
      if (heading.id === headingId) {
        return {
          ...heading,
          functional_issues: heading.functional_issues.map((issue) => {
            if (issue.id === issueId) {
              return { ...issue, comment };
            }
            return issue;
          }),
        };
      }
      return heading;
    });
    setCarePlan(updatedCarePlan);
  };

  // Handle intervention comment updates
  const handleInterventionComment = (
    comment,
    headingId,
    issueId,
    interventionIndex
  ) => {
    const updatedCarePlan = carePlan.map((heading) => {
      if (heading.id === headingId) {
        return {
          ...heading,
          functional_issues: heading.functional_issues.map((issue) => {
            if (issue.id === issueId) {
              const updatedInterventions = [...issue.intervention];
              updatedInterventions[interventionIndex] = {
                ...updatedInterventions[interventionIndex],
                comment,
              };
              return { ...issue, intervention: updatedInterventions };
            }
            return issue;
          }),
        };
      }
      return heading;
    });
    setCarePlan(updatedCarePlan);
  };

  // Transform care plan data to API format
  const transformCarePlanData = (status) => {
    const authorId = JSON.parse(localStorage.getItem("user")).organizations[0]
      .careProvider.id;

    const dataToSubmit = carePlan
      .flatMap((item) => item.functional_issues)
      .map((funcIssue, issueIndex) => {
        const validInterventions = (funcIssue.intervention || [])
          .filter(
            (intervention) =>
              intervention.id ||
              intervention.provider?.id ||
              intervention.position?.id ||
              intervention.review_date
          )
          .map((intervention, interventionIndex) => ({
            labelId: intervention.id || null,
            assigneeId: intervention.provider?.id || null,
            assigneePositionId: intervention.position?.id || null,
            reviewDate: intervention.review_date || null,
            comment: intervention.comment || "",
            orderIndex: intervention.orderIndex ?? interventionIndex,
          }));

        const subheadingId = funcIssue.subheading?.id || null;
        const currentScore = funcIssue.current_score || null;
        const assignedDate = funcIssue.current_score_date || null;

        if (status === "DRAFT") {
          return {
            currentScore,
            targetScore: funcIssue.target_score || null,
            assignedDate,
            subheadingId,
            comment: funcIssue.comment || "",
            orderIndex: issueIndex,
            interventions: validInterventions,
          };
        } else {
          // For SIGNED: include any issue that has at minimum a subheading
          // selected, even with no score yet. On amend the current score isn't
          // auto-filled, so a minimally-added issue (subheading only) must still
          // be sent — requiring a score here silently dropped it.
          if (subheadingId) {
            return {
              currentScore,
              targetScore: funcIssue.target_score || null,
              assignedDate,
              subheadingId,
              comment: funcIssue.comment || "",
              orderIndex: issueIndex,
              interventions: validInterventions,
            };
          }
          return undefined;
        }
      })
      .filter((item) => item !== undefined);

    // Prepare attendee IDs
    let attendeeIds = [authorId];
    if (
      carePlanAttendees?.attendees &&
      carePlanAttendees.attendees.length > 0
    ) {
      attendeeIds = [...carePlanAttendees.attendees];
      if (!attendeeIds.includes(authorId)) {
        attendeeIds.push(authorId);
      }
    }

    const getLocalISOString = () => {
      const now = new Date();
      const offset = -now.getTimezoneOffset();
      const sign = offset >= 0 ? "+" : "-";
      const absOffset = Math.abs(offset);
      const hh = String(Math.floor(absOffset / 60)).padStart(2, "0");
      const mm = String(absOffset % 60).padStart(2, "0");
      const pad = (n) => String(n).padStart(2, "0");
      return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}${sign}${hh}:${mm}`;
    };

    // Build the payload based on care plan type
    const payload = {
      entryDate: carePlanDate || date,
      dateCreated: getLocalISOString(),
      patientId: parseInt(patientId),
      authorId,
      type: carePlanType?.value || "INDIVIDUAL",
      status,
      attendeeIds,
      issues: dataToSubmit,
      // Only sent when a reason is required (amend, or editing a version > 1).
      // The backend stores it only on amended versions and ignores a blank
      // value, so this is a no-op on originals.
      ...(requiresEditReason &&
        editReason.trim() && { editReason: editReason.trim() }),
    };

    // Add collaborative care plan specific fields
    if (carePlanType?.value === "COLLABORATIVE" && carePlanMeetingTypeId != null) {
      payload.meetingTypeId = carePlanMeetingTypeId;
    }

    return payload;
  };

  // Persist the care plan with the given status.
  // - Amend mode (source is SIGNED): create a new version via amend, then edit
  //   that new draft with the user's changes (and the chosen status).
  // - Otherwise: edit the draft in place, or create a brand new plan.
  const persistCarePlan = (status) => {
    const payload = transformCarePlanData(status);

    if (isAmendMode) {
      // SIGNED plans can't be edited in place — amend to create a new version.
      amendMutate(
        { carePlanId: finalCarePlanId, editReason: editReason.trim() },
        {
          onSuccess: (res) => {
            const newId = res?.data?.id;
            if (!newId) {
              toast.error("Could not create a new version. Please try again.");
              setAllowNavigation(false);
              return;
            }
            updateMutate({ carePlanId: newId, carePlan: payload });
          },
          onError: () => {
            toast.error("Could not create a new version. Please try again.");
            setAllowNavigation(false);
          },
        }
      );
    } else if (finalCarePlanId) {
      // Update existing draft care plan
      updateMutate({ carePlanId: finalCarePlanId, carePlan: payload });
    } else {
      // Create new care plan
      mutate(payload);
    }
  };

  // A non-blank reason is required before amending a signed plan or saving an
  // edit to any version > 1.
  const validateEditReason = () => {
    if (requiresEditReason && !editReason.trim()) {
      toast.warn("Please provide a reason for creating another version", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "light",
        transition: Bounce,
      });
      return false;
    }
    return true;
  };

  // Handle save for later (DRAFT status)
  const handleSaveForLater = () => {
    if (!validateEditReason()) return;
    setAllowNavigation(true); // Allow navigation after save
    setActionType("SAVE_FOR_LATER"); // Set action type for navigation
    persistCarePlan("DRAFT");
  };

  // Handle sign (SIGNED status)
  const handleSign = () => {
    if (!validateEditReason()) return;
    setAllowNavigation(true); // Allow navigation after sign
    setActionType("SIGN"); // Set action type for navigation
    persistCarePlan("SIGNED");
  };

  // Handle clear button actions
  const handleClearAction = () => {
    // Show clear confirmation modal instead of clearing immediately
    setShowClearModal(true);
  };

  const openAcceptChangesModal = (onConfirm) => {
    setAcceptChangesAction(() => onConfirm);
    setShowAcceptChangesModal(true);
  };

  const handleConfirmAcceptChanges = () => {
    if (acceptChangesAction) {
      acceptChangesAction();
    }
    setShowAcceptChangesModal(false);
    setAcceptChangesAction(null);
  };

  const handleCancelAcceptChanges = () => {
    setShowAcceptChangesModal(false);
    setAcceptChangesAction(null);
  };

  const openClearRowModal = (onConfirm) => {
    setClearRowAction(() => onConfirm);
    setShowClearRowModal(true);
  };

  const handleConfirmClearRow = () => {
    if (clearRowAction) {
      clearRowAction();
    }
    setShowClearRowModal(false);
    setClearRowAction(null);
  };

  const handleCancelClearRow = () => {
    setShowClearRowModal(false);
    setClearRowAction(null);
  };

  // Handle clear confirmation
  const handleConfirmClear = () => {
    setShowClearModal(false);
    setAllowNavigation(true);
    // Clear sessionStorage flag since user is clearing data
    sessionStorage.removeItem("hasUnsavedCarePlan");
    // Navigate back to care management without the care plan data
    setTimeout(() => {
      props.history.push("/hcp/patient/care_management");
    }, 0);
  };

  // Handle cancel clear
  const handleCancelClear = () => {
    setShowClearModal(false);
  };

  // Show loading state when fetching outstanding care plan or other data
  if (
    (finalFromOutstanding && isLoadingOutstanding) ||
    isLoadingInterv ||
    isLoadingPositions ||
    isLoadingProviders ||
    isLoadingFunctionalChecklist
  ) {
    return (
      <SkeletonTheme baseColor="#e9ecef" highlightColor="#f8f9fa">
        <div className="container-fluid pt-12 pl-21 pb-12 pr-24 h-100 noScroll">
          <div className="card border-0 h-100 verticalScroll">
            <div className="card-body mx-0">
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="font-weight-lighter">HCP: REVIEW Care Plan</h5>
                <div className="media align-items-center">
                  <h6>
                    <small>
                      Meeting/Entry Date:{" "}
                      <sup>
                        <FaInfoCircle />
                      </sup>
                    </small>
                  </h6>
                  <div className="media-body ml-2" style={{ width: "160px" }}>
                    <small>
                      <Skeleton height={12} width={120} />
                    </small>
                  </div>
                </div>
              </div>
              <hr />

              {/* Care Plan Type and Meeting Info Skeleton */}
              <div className="row mb-3">
                <div className="col-md-4">
                  <div className="border rounded p-2">
                    <small className="text-muted">Care Plan Type:</small>
                    <div>
                      <small>
                        <Skeleton height={12} width={150} />
                      </small>
                    </div>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="border rounded p-2">
                    <small className="text-muted">Meeting Type:</small>
                    <div>
                      <small>
                        <Skeleton height={12} width={120} />
                      </small>
                    </div>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="border rounded p-2">
                    <small className="text-muted">Attendees:</small>
                    <div>
                      <small>
                        <Skeleton height={12} width={80} />
                      </small>
                    </div>
                  </div>
                </div>
              </div>

              <div className="py_2">
                <div className="mb-4 py-0">
                  {/* Skeleton Accordions */}
                  {[1, 2].map((accordionIndex) => (
                    <div key={accordionIndex} className="mb-2">
                      <div className="chevron" />
                      <div>
                        {/* Accordion Header */}
                        <div
                          className="accordion__primary accordion__button"
                          style={{
                            height: 45,
                            display: "flex",
                            alignItems: "center",

                            padding: "0 15px",
                            borderRadius: "4px",
                          }}
                        >
                          <small>
                            <Skeleton height={12} width={200} />
                          </small>
                        </div>

                        {/* Accordion Content */}
                        <div>
                          {/* Table Headers */}
                          <div className="row text-center text-white head_light mx-0 h-27">
                            <div className="col-md-3 border-1">
                              <small className="d-flex justify-content-center align-items-center h-100 f-11">
                                Functional Issue
                              </small>
                            </div>
                            <div className="col border-1 px-4" style={{ order: 3 }}>
                              <small className="f-11">{carePlanType?.value === "COLLABORATIVE" ? "Initial Team Score" : "Initial Ind Score"}</small>
                            </div>
                            <div className="col border-1 text-center px-4" style={{ order: 4 }}>
                              <small className="f-11">Assigned Date</small>
                            </div>
                            <div className="col border-1 text-center px-4" style={{ order: 1 }}>
                              <small className="f-11">{carePlanType?.value === "COLLABORATIVE" ? "Current Team Score" : "Current Ind Score"}</small>
                            </div>
                            <div className="col border-1 text-center px-4" style={{ order: 2 }}>
                              <small className="f-11">Assigned Date</small>
                            </div>
                            <div className="col border-1 text-center px-4" style={{ order: 5 }}>
                              <small className="f-11">{carePlanType?.value === "COLLABORATIVE" ? "Target Team Score" : "Target Ind Score"}</small>
                            </div>
                            <div className="col-md-3 border-1" style={{ order: 6 }}>
                              <small className="d-flex justify-content-center align-items-center h-100 f-11">
                                Comment
                              </small>
                            </div>
                            <div className="col border-1" style={{ order: 7 }}>
                              <small className="d-flex justify-content-center align-items-center h-100 f-11">
                                Actions
                              </small>
                            </div>
                          </div>

                          {/* Functional Issue Rows */}
                          {[1].map((issueIndex) => (
                            <div key={issueIndex}>
                              {/* Main Functional Issue Row */}
                              <div className="row text-center mx-0 mt-1">
                                {/* Functional Issue */}
                                <div className="col-md-3 px-0">
                                  <div className="dropdown">
                                    <button
                                      className="btn btn-block border"
                                      type="button"
                                      disabled
                                    >
                                      <div className="d-flex align-items-center justify-content-between">
                                        <small className="f-11">
                                          <Skeleton height={12} width={120} />
                                        </small>
                                        <BiChevronDown
                                          size={ICON_SIZE}
                                          className="navbar-btn-icon"
                                        />
                                      </div>
                                    </button>
                                  </div>
                                </div>

                                {/* Initial Team Score */}
                                <div className="col border-1 px-4 d-flex align-items-center justify-content-center" style={{ order: 3 }}>
                                  <div className="d-flex align-items-center">
                                    {[1, 2, 3, 4, 5].map((score) => (
                                      <div
                                        key={score}
                                        className="mx-1 d-flex align-items-center justify-content-center"
                                        style={{
                                          width: "40px",
                                          height: "40px",
                                        }}
                                      >
                                        <Skeleton height={40} width={40} />
                                      </div>
                                    ))}
                                  </div>
                                </div>

                                {/* Assigned Date */}
                                <div className="col border-1 text-center px-4 d-flex align-items-center justify-content-center" style={{ order: 4 }}>
                                  <small>
                                    <Skeleton height={12} width={80} />
                                  </small>
                                </div>

                                {/* Current Team Score */}
                                <div className="col border-1 text-center px-4 d-flex align-items-center justify-content-center" style={{ order: 1 }}>
                                  <div className="d-flex align-items-center">
                                    {[1, 2, 3, 4, 5].map((score) => (
                                      <div
                                        key={score}
                                        className="mx-1 d-flex align-items-center justify-content-center"
                                        style={{
                                          width: "40px",
                                          height: "40px",
                                        }}
                                      >
                                        <Skeleton height={40} width={40} />
                                      </div>
                                    ))}
                                  </div>
                                </div>

                                {/* Current Assigned Date */}
                                <div className="col border-1 text-center px-4 d-flex align-items-center justify-content-center" style={{ order: 2 }}>
                                  <small>
                                    <Skeleton height={12} width={80} />
                                  </small>
                                </div>

                                {/* Target Score */}
                                <div className="col border-1 text-center px-4 d-flex align-items-center justify-content-center" style={{ order: 5 }}>
                                  <div className="d-flex align-items-center">
                                    {[1, 2, 3, 4, 5].map((score) => (
                                      <div
                                        key={score}
                                        className="mx-1 d-flex align-items-center justify-content-center"
                                        style={{
                                          width: "40px",
                                          height: "40px",
                                        }}
                                      >
                                        <Skeleton height={40} width={40} />
                                      </div>
                                    ))}
                                  </div>
                                </div>

                                {/* Comment */}
                                <div className="col-md-3 d-flex align-items-center" style={{ order: 6 }}>
                                  <div
                                    className="form-control border py-0 f-11 text-center outline-none"
                                    style={{
                                      borderRadius: "20px",
                                      backgroundColor: "#f8f9fa",
                                    }}
                                  >
                                    <small>
                                      <Skeleton height={12} width="100%" />
                                    </small>
                                  </div>
                                </div>

                                {/* Actions */}
                                <div className="col border-1 d-flex align-items-center justify-content-center" style={{ order: 7 }}>
                                  <small>
                                    <Skeleton height={12} width={20} />
                                  </small>
                                </div>
                              </div>

                              {/* Intervention Rows */}
                              {[1, 2].map((intervIndex) => (
                                <div
                                  key={intervIndex}
                                  className="row text-center mx-0 align-items-center"
                                >
                                  {/* Intervention */}
                                  <div className="col-md-3 pl-5 pr-0">
                                    <div className="dropdown">
                                      <button
                                        className="btn btn-block border px-1"
                                        type="button"
                                        disabled
                                      >
                                        <div className="d-flex align-items-center justify-content-between">
                                          <small className="f-11">
                                            <Skeleton height={12} width={100} />
                                          </small>
                                          <BiChevronDown
                                            size={ICON_SIZE}
                                            className="navbar-btn-icon"
                                          />
                                        </div>
                                      </button>
                                    </div>
                                  </div>

                                  <div className="col-md-1 border-1 text-right d-flex align-items-center justify-content-end">
                                    <small className="f-8">
                                      Assigned <br /> to:
                                    </small>
                                  </div>

                                  {/* Position */}
                                  <div className="col-md-1 px-1">
                                    <div className="dropdown">
                                      <button
                                        className="btn btn-block border px-1"
                                        type="button"
                                        disabled
                                      >
                                        <div className="d-flex align-items-center justify-content-between">
                                          <small className="f-11">
                                            <Skeleton height={12} width={30} />
                                          </small>
                                          <BiChevronDown
                                            size={ICON_SIZE}
                                            className="navbar-btn-icon"
                                          />
                                        </div>
                                      </button>
                                    </div>
                                  </div>

                                  {/* Provider */}
                                  <div className="col-md-2 px-1">
                                    <div className="dropdown">
                                      <button
                                        className="btn btn-block border px-1"
                                        type="button"
                                        disabled
                                      >
                                        <div className="d-flex align-items-center justify-content-between">
                                          <small className="f-11">
                                            <Skeleton height={12} width={80} />
                                          </small>
                                          <BiChevronDown
                                            size={ICON_SIZE}
                                            className="navbar-btn-icon"
                                          />
                                        </div>
                                      </button>
                                    </div>
                                  </div>

                                  {/* Review Date */}
                                  <div className="col-md-2 p-0 my-0 d-flex align-items-center">
                                    <div className="form-group row m-0 w-100">
                                      <label className="col-4 pr-1 col-form-label text-right p-0">
                                        <small className="f-8">
                                          Review <br /> By:
                                        </small>
                                      </label>
                                      <div className="col-8 pl-2 p-0">
                                        <small>
                                          <Skeleton height={12} width={60} />
                                        </small>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Comment */}
                                  <div className="col-md-2 d-flex align-items-center">
                                    <div
                                      className="form-control border py-0 f-11 text-center outline-none"
                                      style={{
                                        borderRadius: "20px",
                                        backgroundColor: "#f8f9fa",
                                      }}
                                    >
                                      <small>
                                        <Skeleton height={12} width="100%" />
                                      </small>
                                    </div>
                                  </div>

                                  {/* Actions */}
                                  <div className="col-md-1 border-1 d-flex align-items-center justify-content-center">
                                    <small>
                                      <Skeleton height={12} width={20} />
                                    </small>
                                  </div>
                                </div>
                              ))}

                              {/* Add another intervention button */}
                              <div className="row mx-0 mt-2">
                                <div className="col-md-3 pl-5">
                                  <button className="btn pl-1" disabled>
                                    <div className="text_primary">
                                      <b>+</b>{" "}
                                      <small className="f-11">
                                        <Skeleton height={12} width={120} />
                                      </small>
                                    </div>
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}

                          {/* Add another functional issue button */}
                          <button className="btn pl-1" disabled>
                            <div className="mt-0 pt-0 text_primary">
                              <b>+</b>{" "}
                              <small className="f-12">
                                <Skeleton height={12} width={150} />
                              </small>
                            </div>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        <FooterButton
          child1={
            <button className="btn btn_danger px-5" disabled>
              <small>
                <Skeleton height={12} width={50} />
              </small>
            </button>
          }
          child2={
            <div>
              <button className="btn btn_outline_light px-3 mx-3" disabled>
                <FiChevronLeft size={ICON_SIZE} />
                <small>
                  <Skeleton height={12} width={120} />
                </small>
              </button>
              <button className="btn btn_outline_light px-3 mx-3" disabled>
                <small>
                  <Skeleton height={12} width={100} />
                </small>
              </button>
              <button className="btn btn_light px-5 pr-2 ml-3" disabled>
                <small>
                  <Skeleton height={12} width={40} />
                </small>
              </button>
            </div>
          }
        />
      </SkeletonTheme>
    );
  }

  // Don't show "no data" message if we're still loading outstanding data or if we have outstanding data
  const hasDate = finalDate || carePlanDate;
  if (
    (!hasDate || !carePlan || carePlan.length === 0) &&
    !(finalFromOutstanding && (isLoadingOutstanding || outstandingCarePlan))
  ) {
    return (
      <div className="container-fluid pt-12 pl-21 pb-12 pr-24 h-100 noScroll">
        <div className="card border-0 h-100 verticalScroll">
          <div className="card-body mx-0">
            <h5 className="font-weight-lighter">HCP: REVIEW Care Plan</h5>
            <hr />
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ height: "400px" }}
            >
              <p className="text-muted">
                No care plan data available to review.
                <br />
                Debug: date={hasDate ? "exists" : "missing"}, carePlan length=
                {carePlan?.length || 0}, finalFromOutstanding=
                {finalFromOutstanding ? "true" : "false"}, finalCarePlanId=
                {finalCarePlanId || "undefined"}, isLoadingOutstanding=
                {isLoadingOutstanding ? "true" : "false"}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }
  return (
    <>
      {hasDataBeenEntered && (
        <Prompt when={!allowNavigation} message={handleNavigationAttempt} />
      )}
      <div className="container-fluid pt-12 pl-21 pb-12 pr-24 h-100 noScroll">
        <div className="card border-0 h-100 verticalScroll">
          <div className="card-body mx-0 d-flex flex-column h-100">
            <div className="flex-shrink-0">
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="font-weight-lighter">HCP: REVIEW Care Plan</h5>
                <div className="d-flex align-items-center">
                  <button
                    type="button"
                    className="btn btn-primary mr-4"
                    onClick={() => setShowLatestScoresOverlay(true)}
                    aria-haspopup="dialog"
                    aria-expanded={showLatestScoresOverlay}
                  >
                    <small>Functional Summaries</small>
                  </button>
                  <div className="media align-items-center">
                    <h6>
                      <small>
                        Meeting/Entry Date:{" "}
                        <sup>
                          <FaInfoCircle />
                        </sup>
                      </small>
                    </h6>
                    <div className="media-body ml-2" style={{ width: "160px" }}>
                      <PickDate
                        date={carePlanDate || finalDate}
                        setDate={setCarePlanDate}
                        iconSize={ICON_SIZE}
                        maxDate={new Date()}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <hr />

              {/* Reason for creating another version — required when amending a
                  signed plan or editing any version > 1. The care-plan headings
                  below stay hidden until this is filled in. */}
              {requiresEditReason && (
                <div className="mb-3">
                  <small className="text-danger">*</small>
                  <div className="d-flex align-items-center">
                    <input
                      type="text"
                      className="form-control"
                      style={{ width: "60%" }}
                      placeholder="mandatory - complete reason for creating another version"
                      maxLength={1000}
                      value={editReason}
                      onChange={(e) => setEditReason(e.target.value)}
                    />
                    {/* The arrow only confirms a freshly typed reason to reveal
                        the body. A draft that already has a stored reason is
                        pre-confirmed, so the arrow is hidden. */}
                    {!editReasonConfirmed && (
                      <button
                        type="button"
                        className="btn p-0 reason-submit-btn ml-2 d-flex align-items-center justify-content-center"
                        title="Submit reason"
                        disabled={!editReason.trim()}
                        onClick={() =>
                          editReason.trim() && setEditReasonConfirmed(true)
                        }
                        style={{
                          width: 30,
                          height: 30,
                          minWidth: 30,
                          borderRadius: 20,
                        }}
                      >
                        <FiArrowRight size={28} color="#fff" strokeWidth={4} />
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Care Plan Type and Meeting Info — hidden until the reason is
                  submitted */}
              {showCarePlanBody && (
              <div className="row mb-3">
                <div className="col-md-4">
                  <div className="border rounded p-2">
                    <small className="text-muted">Care Plan Type:</small>
                    <div>
                      <small>{carePlanType?.label || "Not specified"}</small>
                    </div>
                  </div>
                </div>
                {(carePlanMeeting || isEditingMeetingType) && (
                  <div className="col-md-4">
                    <div className="border rounded p-2">
                      <div className="d-flex align-items-center justify-content-between">
                        <small className="text-muted">Meeting Type:</small>
                        <button
                          type="button"
                          className="btn btn-link p-0"
                          onClick={() =>
                            setIsEditingMeetingType(!isEditingMeetingType)
                          }
                        >
                          <FiEdit className="text_primary" size={ICON_SIZE} />
                        </button>
                      </div>
                      {isEditingMeetingType ? (
                        <div className="dropdown mt-1">
                          <button
                            className="btn py-1 btn-block border text-left bg-white"
                            type="button"
                            id="meetingTypeDropdown"
                            data-toggle="dropdown"
                            aria-expanded="false"
                          >
                            <div className="d-flex justify-content-between align-items-center">
                              <small>
                                {carePlanMeeting || "Choose Type of Meeting"}
                              </small>
                              <BiChevronDown
                                size={ICON_SIZE}
                                className="navbar-btn-icon"
                              />
                            </div>
                          </button>
                          <div
                            className="dropdown-menu custom border-0 shadow pb-3"
                            aria-labelledby="meetingTypeDropdown"
                          >
                            <p className="pl-4 mb-0 pb-2">
                              <small>Choose one type only:</small>
                            </p>
                            {meetingTypesData?.data?.map((item, key) => (
                              <label
                                key={key}
                                className="dropdown-item ml-4"
                                onClick={() => handleMeetingSelection(item)}
                              >
                                <small>{item.name}</small>
                              </label>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div>
                          <small>{carePlanMeeting || "Not specified"}</small>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {carePlanType?.value === "COLLABORATIVE" && (
                    <div className="col-md-4">
                      <div className="border rounded p-2">
                        <div className="d-flex align-items-center justify-content-between">
                          <small className="text-muted">Attendees:</small>
                          <button
                            type="button"
                            className="btn btn-link p-0"
                            onClick={() => {
                              const next = !isEditingAttendees;
                              setIsEditingAttendees(next);
                              setShowAttendeesList(next);
                            }}
                          >
                            <FiEdit className="text_primary" size={ICON_SIZE} />
                          </button>
                        </div>
                        {isEditingAttendees ? (
                          <div className="dropdown mt-1">
                            <button
                              className="btn py-1 btn-block border text-left bg-white"
                              type="button"
                              aria-expanded="false"
                              onClick={() =>
                                setShowAttendeesList(!showAttendeesList)
                              }
                            >
                              <div className="d-flex justify-content-between align-items-center">
                                <small>
                                  Confirm Attendees:{" "}
                                  {carePlanAttendees?.attendees?.length || 0}
                                </small>
                                <BiChevronDown
                                  size={ICON_SIZE}
                                  className="navbar-btn-icon"
                                />
                              </div>
                            </button>
                            <div
                              className={`dropdown-menu custom border-0 shadow ${
                                showAttendeesList ? "show" : ""
                              }`}
                            >
                              <p className="pl-4 mb-0 pb-2">
                                <small>Choose as many as applicable:</small>
                              </p>
                              {attendeeOptions.map((item, key) => (
                                <div
                                  key={key}
                                  className="form-group form-check my-3 p-0 ml-4"
                                >
                                  <input
                                    type="checkbox"
                                    className="form-check-input mt-2"
                                    checked={carePlanAttendees?.attendees?.includes(
                                      item?.id
                                    )}
                                    onChange={() =>
                                      handleCollaborativeAttendees(item)
                                    }
                                  />
                                  &nbsp;&nbsp;&nbsp;
                                  <label className="form-check-label">
                                    <small>
                                      {item.lastName}, {item.firstName}:{" "}
                                      {item.position?.abbreviation}
                                    </small>
                                  </label>
                                </div>
                              ))}
                              {attendeeOptions.length === 0 && (
                                <p className="pl-4 mb-0 pb-2 text-muted">
                                  <small>
                                    No other care providers are assigned to this
                                    patient
                                  </small>
                                </p>
                              )}
                              <div className="d-flex justify-content-between align-items-center mx-3 mb-2 mt-4">
                                <button
                                  className="btn btn_outline_danger px-4"
                                  onClick={handleRemoveAttendees}
                                >
                                  <small className="mx-3">Clear</small>
                                </button>
                                <button
                                  className="btn btn-primary px-4"
                                  onClick={handleConfirmedAttendees}
                                >
                                  <small className="mx-3">Save</small>
                                </button>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div>
                            <small>
                              {carePlanAttendees?.attendees?.length || 0}{" "}
                              confirmed
                            </small>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
              </div>
              )}
            </div>

            {showCarePlanBody && (
            <div className="flex-grow-1 py_2" style={{ minHeight: 0 }}>
              <Accordion
                allowZeroExpanded
                allowMultipleExpanded
                preExpanded={[0]}
                className="mb-4 py-0"
              >
                {carePlan.map((careheading, i) => (
                  <div key={i}>
                    <div className="chevron" />
                    <AccordionItem uuid={i} className="mb-2">
                      <AccordionItemHeading className="accordion__primary">
                        <AccordionItemButton
                          style={{
                            height: 45,
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <small>{careheading.heading.name}</small>
                        </AccordionItemButton>
                      </AccordionItemHeading>
                      <AccordionItemPanel>
                        <div className="row text-center text-white head_light mx-0 h-35">
                          <div className="col-md-3 border-1">
                            <small className="d-flex justify-content-center align-items-center h-100 f-11">
                              Functional Issue
                            </small>
                          </div>
                          <div className="col border-1 px-4" style={{ order: 3 }}>
                            <small className="d-flex justify-content-center align-items-center h-100 f-11">
                              {carePlanType?.value === "COLLABORATIVE" ? "Initial Team Score" : "Initial Ind Score"}
                            </small>
                          </div>
                          <div className="col border-1 text-center px-4" style={{ order: 4 }}>
                            <small className="d-flex justify-content-center align-items-center h-100 f-11">
                              Assigned Date
                            </small>
                          </div>
                          <div className="col border-1 text-center px-4" style={{ order: 1 }}>
                            <small className="d-flex justify-content-center align-items-center h-100 f-11">
                              {carePlanType?.value === "COLLABORATIVE" ? "Current Team Score" : "Current Ind Score"}
                            </small>
                          </div>
                          <div className="col border-1 text-center px-4" style={{ order: 2 }}>
                            <small className="d-flex justify-content-center align-items-center h-100 f-11">
                              Assigned Date
                            </small>
                          </div>
                          <div className="col border-1 text-center px-4" style={{ order: 5 }}>
                            <small className="d-flex justify-content-center align-items-center h-100 f-11">
                              {carePlanType?.value === "COLLABORATIVE" ? "Target Team Score" : "Target Ind Score"}
                            </small>
                          </div>
                          <div className="col-md-2 border-1 d-flex justify-content-center align-items-center" style={{ order: 6 }}>
                            <small className="f-11">Comment</small>
                          </div>
                          <div className="col-md-1 border-1 d-flex justify-content-center align-items-center" style={{ order: 7 }}>
                            <small className="f-11">Actions</small>
                          </div>
                        </div>

                        {getDisplayIssues(careheading).map(
                          (item, issueIndex) => (
                            <div key={issueIndex}>
                              <div
                                className="row text-center mx-0 mt-1"
                                style={{
                                  minHeight: "38px",
                                  position: "relative",
                                }}
                              >
                                {/* Functional Issue */}
                                <div className="col-md-3 px-1 d-flex align-items-center">
                                  <div className="dropdown w-100 d-flex align-items-center">
                                    <button
                                      className="btn btn-block py-1 border bg-white"
                                      type="button"
                                      id="dropdownMenuButton"
                                      onClick={item.isEdit ? (e) => {
                                        e.stopPropagation();
                                        const key = `func_issue-${careheading.id}-${item.id}`;
                                        setOpenDropdownKey(openDropdownKey === key ? null : key);
                                      } : undefined}
                                      aria-expanded="false"
                                      style={{
                                        height: "38px",
                                        padding: "0 12px",
                                        borderRadius: "4px",
                                      }}
                                    >
                                      <div className="d-flex align-items-center justify-content-between">
                                        <small className="f-11">
                                          {item.subheading?.name ||
                                            "Select Issue"}
                                        </small>
                                        <BiChevronDown
                                          size={ICON_SIZE}
                                          className="navbar-btn-icon"
                                        />
                                      </div>
                                    </button>
                                    {item.isEdit && openDropdownKey === `func_issue-${careheading.id}-${item.id}` && functionalChecklistData && (
                                      <div className="dropdown-menu custom border-0 shadow show">
                                        <p className="pl-4 mb-0 pb-2">
                                          <small className="small-text">
                                            Choose one issue only:
                                          </small>
                                        </p>
                                        {(() => {
                                          const foundHeading =
                                            functionalChecklistData.data?.[0]?.headings?.find(
                                              (heading) =>
                                                heading.name ===
                                                careheading.heading.name
                                            );
                                          return foundHeading?.subheadings?.map(
                                            (subheading, key) => (
                                              <a
                                                className="dropdown-item ml-4"
                                                href="#"
                                                key={key}
                                                onClick={(e) => {
                                                  e.preventDefault();
                                                  handleIssueSelection(
                                                    careheading.id,
                                                    item.id,
                                                    {
                                                      id: subheading.id,
                                                      name: subheading.name,
                                                    }
                                                  );
                                                  setOpenDropdownKey(null);
                                                }}
                                              >
                                                <small className="small-text">
                                                  {subheading.name}
                                                </small>
                                              </a>
                                            )
                                          );
                                        })()}
                                      </div>
                                    )}
                                  </div>
                                </div>

                                {/* Initial Team Score */}
                                <div className="col px-1 d-flex align-items-center" style={{ order: 3 }}>
                                  <div className="dropdown w-100">
                                    <button
                                      className={`btn btn-block py-1 border px-1 bg-white ${
                                        item.initial_score &&
                                        "bg-transparent border-0"
                                      }`}
                                      type="button"
                                      id="dropdownMenuButton"
                                      onClick={item.isEdit && !(item.subheading && baselineScores?.[item.subheading.id]) ? (e) => {
                                        e.stopPropagation();
                                        const key = `initial_score-${careheading.id}-${item.id}`;
                                        setOpenDropdownKey(openDropdownKey === key ? null : key);
                                      } : undefined}
                                      disabled={!!(item.subheading && baselineScores?.[item.subheading.id])}
                                      title={item.subheading && baselineScores?.[item.subheading.id] ? "Initial score is fixed for this admission" : undefined}
                                      aria-expanded="false"
                                      style={{
                                        borderRadius: "4px",
                                        height: "38px",
                                        padding: "0 12px",
                                        cursor: item.subheading && baselineScores?.[item.subheading.id] ? "default" : undefined,
                                        opacity: 1,
                                      }}
                                    >
                                      <div className="d-flex align-items-center justify-content-center position-relative">
                                        {item && item.initial_score ? (
                                          <div
                                            className={`rounded-pill d-flex align-items-center justify-content-center font-weight-bold ${getScoreOptionPillProps(resolveScale(item.subheading?.id), item.initial_score).className}`}
                                            style={{
                                              width: "28px",
                                              height: "28px",
                                              fontSize: "12px",
                                              minWidth: "28px",
                                             ...getScoreOptionPillProps(resolveScale(item.subheading?.id), item.initial_score).style,}}
                                          >
                                            {item.initial_score}
                                          </div>
                                        ) : (
                                          <small className="f-11">Select</small>
                                        )}
                                        {!(item.subheading && baselineScores?.[item.subheading.id]) && (
                                          <BiChevronDown
                                            size={ICON_SIZE}
                                            className="navbar-btn-icon"
                                            style={{
                                              position: "absolute",
                                              right: "4px",
                                            }}
                                          />
                                        )}
                                      </div>
                                    </button>
                                    {item.isEdit && openDropdownKey === `initial_score-${careheading.id}-${item.id}` && (
                                      <div className="dropdown-menu small border-0 shadow show">
                                        <p className="pl-4 mb-0 pb-2">
                                          <small>Choose one score only:</small>
                                        </p>
                                        {getScore(resolveScale(item.subheading?.id))
                                          .sort((a, b) => {
                                            if (a.value === "N/A") return 1;
                                            if (b.value === "N/A") return -1;
                                            return b.value - a.value;
                                          })
                                          .map((score, key) => (
                                            <a
                                              className={`dropdown-item ml-4 border_primary mr-2 mb-1 rounded-5 h-35 ${
                                                item.initial_score ===
                                                score.value
                                                  ? "bg-light"
                                                  : ""
                                              }`}
                                              key={key}
                                              href="#"
                                              onClick={(e) => {
                                                e.preventDefault();
                                                handleScore(
                                                  score.value,
                                                  "initial_score",
                                                  careheading.id,
                                                  item.id
                                                );
                                                setOpenDropdownKey(null);
                                              }}
                                            >
                                              <small
                                                className={`w-27 h-27 rounded-circle d-flex align-items-center justify-content-center font-weight-bold ${getScoreOptionPillProps(resolveScale(item.subheading?.id), score.value, "head_light f-10").className}`}
                                              style={getScoreOptionPillProps(resolveScale(item.subheading?.id), score.value, "head_light f-10").style}
                                              >
                                                {score.value}
                                              </small>
                                            </a>
                                          ))}
                                      </div>
                                    )}
                                  </div>
                                </div>

                                {/* Assigned Date */}
                                <div className="col px-1 d-flex align-items-center" style={{ order: 4 }}>
                                  <div
                                    style={{
                                      width: "100%",
                                      display: "flex",
                                      justifyContent: "center",
                                    }}
                                  >
                                    <InputDatePicker
                                      date={item.initial_score_date || ""}
                                      setDate={
                                        item.isEdit ? handleAssignedDate : null
                                      }
                                      dateType="initial_score_date"
                                      heading={careheading.id}
                                      issueId={item.id}
                                      intervention={null}
                                      disabled={!item.isEdit || !!(item.subheading && baselineScores?.[item.subheading?.id])}
                                      noOffset
                                      height="38px"
                                      style={{
                                        width: "100%",
                                        margin: "0 auto",
                                        borderRadius: "4px",
                                        padding: "0 12px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                      }}
                                      maxDate={new Date()}
                                    />
                                  </div>
                                </div>

                                {/* Current Team Score — editable in row edit mode */}
                                <div className="col px-1 d-flex align-items-center" style={{ order: 1 }}>
                                  <div className="dropdown w-100">
                                    <button
                                      className={`btn btn-block py-1 border px-1 bg-white ${
                                        item.current_score &&
                                        "bg-transparent border-0"
                                      }`}
                                      type="button"
                                      onClick={item.isEdit ? (e) => {
                                        e.stopPropagation();
                                        const key = `current_score-${careheading.id}-${item.id}`;
                                        setOpenDropdownKey(openDropdownKey === key ? null : key);
                                      } : undefined}
                                      aria-expanded="false"
                                      style={{
                                        borderRadius: "4px",
                                        height: "38px",
                                        padding: "0 12px",
                                        opacity: 1,
                                      }}
                                    >
                                      <div className="d-flex align-items-center justify-content-center position-relative">
                                        {item && item.current_score ? (
                                          <div
                                            className={`rounded-pill d-flex align-items-center justify-content-center font-weight-bold ${getScoreOptionPillProps(resolveScale(item.subheading?.id), item.current_score).className}`}
                                            style={{
                                              width: "28px",
                                              height: "28px",
                                              fontSize: "12px",
                                              minWidth: "28px",
                                             ...getScoreOptionPillProps(resolveScale(item.subheading?.id), item.current_score).style,}}
                                          >
                                            {item.current_score}
                                          </div>
                                        ) : (
                                          <small className="f-11">Select</small>
                                        )}
                                        <BiChevronDown
                                          size={ICON_SIZE}
                                          className="navbar-btn-icon"
                                          style={{
                                            position: "absolute",
                                            right: "4px",
                                          }}
                                        />
                                      </div>
                                    </button>
                                    {item.isEdit && openDropdownKey === `current_score-${careheading.id}-${item.id}` && (
                                      <div className="dropdown-menu small border-0 shadow show">
                                        <p className="pl-4 mb-0 pb-2">
                                          <small>Choose one score only:</small>
                                        </p>
                                        {getScore(resolveScale(item.subheading?.id))
                                          .sort((a, b) => {
                                            if (a.value === "N/A") return 1;
                                            if (b.value === "N/A") return -1;
                                            return b.value - a.value;
                                          })
                                          .map((score, key) => (
                                            <a
                                              className={`dropdown-item ml-4 border_primary mr-2 mb-1 rounded-5 h-35 ${
                                                item.current_score ===
                                                score.value
                                                  ? "bg-light"
                                                  : ""
                                              }`}
                                              key={key}
                                              href="#"
                                              onClick={(e) => {
                                                e.preventDefault();
                                                handleScore(
                                                  score.value,
                                                  "current_score",
                                                  careheading.id,
                                                  item.id
                                                );
                                                setOpenDropdownKey(null);
                                              }}
                                            >
                                              <small
                                                className={`w-27 h-27 rounded-circle d-flex align-items-center justify-content-center font-weight-bold ${getScoreOptionPillProps(resolveScale(item.subheading?.id), score.value, "head_light f-10").className}`}
                                              style={getScoreOptionPillProps(resolveScale(item.subheading?.id), score.value, "head_light f-10").style}
                                              >
                                                {score.value}
                                              </small>
                                            </a>
                                          ))}
                                      </div>
                                    )}
                                  </div>
                                </div>

                                {/* Current Assigned Date — editable in row edit mode */}
                                <div className="col px-1 d-flex align-items-center" style={{ order: 2 }}>
                                  <div
                                    style={{
                                      width: "100%",
                                      display: "flex",
                                      justifyContent: "center",
                                    }}
                                  >
                                    <InputDatePicker
                                      date={item.current_score_date || ""}
                                      setDate={
                                        item.isEdit ? handleAssignedDate : null
                                      }
                                      dateType="current_score_date"
                                      heading={careheading.id}
                                      issueId={item.id}
                                      intervention={null}
                                      disabled={!item.isEdit}
                                      noOffset
                                      height="38px"
                                      style={{
                                        width: "100%",
                                        margin: "0 auto",
                                        borderRadius: "4px",
                                        padding: "0 12px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                      }}
                                      maxDate={new Date()}
                                    />
                                  </div>
                                </div>

                                {/* Target Score */}
                                <div className="col px-1 d-flex align-items-center" style={{ order: 5 }}>
                                  <div className="dropdown w-100">
                                    <button
                                      className={`btn btn-block border py-1 px-1 bg-white ${
                                        item.target_score &&
                                        "bg-transparent border-0"
                                      }`}
                                      type="button"
                                      id="dropdownMenuButton"
                                      onClick={item.isEdit ? (e) => {
                                        e.stopPropagation();
                                        const key = `target_score-${careheading.id}-${item.id}`;
                                        setOpenDropdownKey(openDropdownKey === key ? null : key);
                                      } : undefined}
                                      aria-expanded="false"
                                      style={{
                                        borderRadius: "4px",
                                        height: "38px",
                                        padding: "0 12px",
                                      }}
                                    >
                                      <div className="d-flex align-items-center justify-content-center position-relative">
                                        {item && item.target_score ? (
                                          <div
                                            className={`rounded-pill d-flex align-items-center justify-content-center font-weight-bold ${getScoreOptionPillProps(resolveScale(item.subheading?.id), item.target_score).className}`}
                                            style={{
                                              width: "28px",
                                              height: "28px",
                                              fontSize: "12px",
                                              minWidth: "28px",
                                             ...getScoreOptionPillProps(resolveScale(item.subheading?.id), item.target_score).style,}}
                                          >
                                            {item.target_score}
                                          </div>
                                        ) : (
                                          <small className="f-11">Select</small>
                                        )}
                                        <BiChevronDown
                                          size={ICON_SIZE}
                                          className="navbar-btn-icon"
                                          style={{
                                            position: "absolute",
                                            right: "4px",
                                          }}
                                        />
                                      </div>
                                    </button>
                                    {item.isEdit && openDropdownKey === `target_score-${careheading.id}-${item.id}` && (
                                      <div className="dropdown-menu small border-0 shadow fit show">
                                        <p className="pl-4 mb-0 pb-2">
                                          <small>Choose one score only:</small>
                                        </p>
                                        {getScore(resolveScale(item.subheading?.id))
                                          .sort((a, b) => {
                                            if (a.value === "N/A") return 1;
                                            if (b.value === "N/A") return -1;
                                            return b.value - a.value;
                                          })
                                          .map((score, key) => (
                                            <a
                                              className={`dropdown-item ml-4 border_primary mr-2 mb-1 rounded-5 h-35 ${
                                                item.target_score ===
                                                score.value
                                                  ? "bg-light"
                                                  : ""
                                              }`}
                                              key={key}
                                              href="#"
                                              onClick={(e) => {
                                                e.preventDefault();
                                                handleScore(
                                                  score.value,
                                                  "target_score",
                                                  careheading.id,
                                                  item.id
                                                );
                                                setOpenDropdownKey(null);
                                              }}
                                            >
                                              <small
                                                className={`w-27 h-27 rounded-circle d-flex align-items-center justify-content-center font-weight-bold ${getScoreOptionPillProps(resolveScale(item.subheading?.id), score.value, "head_light f-10").className}`}
                                              style={getScoreOptionPillProps(resolveScale(item.subheading?.id), score.value, "head_light f-10").style}
                                              >
                                                {score.value}
                                              </small>
                                            </a>
                                          ))}
                                      </div>
                                    )}
                                  </div>
                                </div>

                                {/* Comment */}
                                <div className="col-md-2 px-1 d-flex align-items-center" style={{ order: 6 }}>
                                  <input
                                    type="text"
                                    placeholder="Comments"
                                    value={item.comment || ""}
                                    onChange={(e) =>
                                      handleFunctionalIssueComment(
                                        e.target.value,
                                        careheading.id,
                                        item.id
                                      )
                                    }
                                    readOnly={!item.isEdit}
                                    className="form-control border f-11 text-center outline-none bg-white"
                                    style={{
                                      height: "38px",
                                      padding: "0 12px",
                                      width: "100%",
                                      borderRadius: "4px",
                                    }}
                                  />
                                </div>

                                {/* Actions */}
                                <div className="col-md-1 border-1 d-flex align-items-center justify-content-center" style={{ order: 7 }}>
                                  <small
                                    className={`cursor-pointer ${
                                      !item.isEdit ? "d-inline-block" : "d-none"
                                    }`}
                                    onClick={() =>
                                      handleIssueEdit(
                                        careheading.id,
                                        item.id,
                                        true
                                      )
                                    }
                                  >
                                    <FiEdit
                                      className="text_primary"
                                      size={ICON_SIZE}
                                    />
                                  </small>
                                  <small
                                    className={`cursor-pointer ${
                                      item.isEdit ? "d-inline-block" : "d-none"
                                    }`}
                                    title="Accept all changes"
                                    onClick={() =>
                                      openAcceptChangesModal(() =>
                                        handleIssueEdit(
                                          careheading.id,
                                          item.id,
                                          false
                                        )
                                      )
                                    }
                                  >
                                    <FiSave
                                      className="text_primary"
                                      size={ICON_SIZE}
                                    />
                                  </small>
                                  <small
                                    className={`cursor-pointer ml-2 ${
                                      item.isEdit ? "d-inline-block" : "d-none"
                                    }`}
                                    title="Erase all scores and comments"
                                    onClick={() =>
                                      openClearRowModal(() =>
                                        handleClearFunctionalIssue(
                                          careheading.id,
                                          item.id
                                        )
                                      )
                                    }
                                  >
                                    <FiTrash2
                                      className="text_danger"
                                      size={ICON_SIZE}
                                    />
                                  </small>
                                </div>
                              </div>

                              {/* Interventions */}
                              <div className="mt-3">
                                {item.intervention.map(
                                  (intervention, intervIndex) => (
                                    <div
                                      key={intervIndex}
                                      className="row text-center mx-0 mt-1 align-items-center"
                                      style={{
                                        minHeight: "38px",
                                        position: "relative",
                                      }}
                                    >
                                      {/* Intervention */}
                                      <div className="col-md-3 pl-5 pr-0 d-flex align-items-center">
                                        <div className="dropdown w-100">
                                          <button
                                            className="btn btn-block border px-1 text-left"
                                            type="button"
                                            id="dropdownMenuButton"
                                            {...(intervention.isEdit && {
                                              "data-toggle": "dropdown",
                                            })}
                                            aria-expanded="false"
                                            style={{
                                              height: "38px",
                                              padding: "0 12px",
                                            }}
                                          >
                                            <div className="d-flex align-items-center justify-content-between w-100">
                                              <small className="f-11">
                                                {intervention.name ||
                                                  "Select Intervention"}
                                              </small>
                                              <BiChevronDown
                                                size={ICON_SIZE}
                                                className="navbar-btn-icon"
                                              />
                                            </div>
                                          </button>
                                          {intervention.isEdit &&
                                            interventions && (
                                              <div className="dropdown-menu small border-0 shadow unfit">
                                                <p className="pl-4 mb-0 pb-2">
                                                  <small className="small-text">
                                                    Choose one intervention
                                                    only:
                                                  </small>
                                                </p>
                                                {Object.keys(
                                                  interventions.data
                                                ).map((interv, key) => (
                                                  <div key={key}>
                                                    <p className="pl-4 mb-0 pb-2">
                                                      <small className="small-text">
                                                        {interv}:
                                                      </small>
                                                    </p>
                                                    {interventions.data[
                                                      interv
                                                    ].map((action, i) => (
                                                      <a
                                                        key={i}
                                                        className="dropdown-item ml-4 mr-5"
                                                        href="#"
                                                        onClick={(e) => {
                                                          e.preventDefault();
                                                          handleChooseIntervention(
                                                            {
                                                              id: action.id,
                                                              name: action.name,
                                                            },
                                                            intervIndex,
                                                            careheading.id,
                                                            item.id
                                                          );
                                                        }}
                                                      >
                                                        <small className="small-text">
                                                          {action.name}
                                                        </small>
                                                      </a>
                                                    ))}
                                                  </div>
                                                ))}
                                              </div>
                                            )}
                                        </div>
                                      </div>

                                      <div className="col-md-1 border-1 text-right d-flex align-items-center justify-content-end">
                                        <small className="f-8">
                                          Assigned <br /> to:
                                        </small>
                                      </div>

                                      {/* Position */}
                                      <div className="col px-1 d-flex align-items-center">
                                        <div className="dropdown w-100">
                                          <button
                                            className="btn btn-block border px-1 text-left"
                                            type="button"
                                            id="dropdownMenuButton"
                                            {...(intervention.isEdit && {
                                              "data-toggle": "dropdown",
                                            })}
                                            aria-expanded="false"
                                            style={{
                                              height: "38px",
                                              padding: "0 12px",
                                            }}
                                          >
                                            <div className="d-flex align-items-center w-100 justify-content-between">
                                              <small className="f-11">
                                                {intervention.position
                                                  ?.abbreviation || "Pos"}
                                              </small>
                                              <BiChevronDown
                                                size={ICON_SIZE}
                                                className="navbar-btn-icon"
                                              />
                                            </div>
                                          </button>
                                          {intervention.isEdit && positions && (
                                            <div className="dropdown-menu small border-0 shadow unfit">
                                              <p className="pl-4 mb-0 pb-2">
                                                <small>
                                                  Choose one position only:
                                                </small>
                                              </p>
                                              {positions.data?.content
                                                .sort((a, b) =>
                                                  a.position.name.localeCompare(
                                                    b.position.name
                                                  )
                                                )
                                                .flatMap((position, key) => {
                                                  if (
                                                    position.position.name ===
                                                      "Physician" &&
                                                    position.position
                                                      .subPositions
                                                  ) {
                                                    return [
                                                      <a
                                                        key={key}
                                                        className="ml-4 pl-1 py-3 decoration-none"
                                                        href="#"
                                                      >
                                                        <small className="small-text font-weight-bold">
                                                          {
                                                            position.position
                                                              .name
                                                          }
                                                          :
                                                        </small>
                                                      </a>,
                                                      ...position.position.subPositions.map(
                                                        (subPosition) => (
                                                          <a
                                                            key={subPosition.id}
                                                            className="dropdown-item ml-4 py-2"
                                                            href="#"
                                                            onClick={(e) => {
                                                              e.preventDefault();
                                                              handlePosition(
                                                                subPosition,
                                                                intervIndex,
                                                                careheading.id,
                                                                item.id
                                                              );
                                                            }}
                                                          >
                                                            <small className="small-text">
                                                              {subPosition.name}{" "}
                                                              (
                                                              {
                                                                subPosition.abbreviation
                                                              }
                                                              )
                                                            </small>
                                                          </a>
                                                        )
                                                      ),
                                                    ];
                                                  } else {
                                                    return (
                                                      <a
                                                        key={key}
                                                        className="dropdown-item ml-1 py-2"
                                                        href="#"
                                                        onClick={(e) => {
                                                          e.preventDefault();
                                                          handlePosition(
                                                            position.position,
                                                            intervIndex,
                                                            careheading.id,
                                                            item.id
                                                          );
                                                        }}
                                                      >
                                                        <small className="small-text">
                                                          {
                                                            position.position
                                                              .name
                                                          }{" "}
                                                          (
                                                          {
                                                            position.position
                                                              .abbreviation
                                                          }
                                                          )
                                                        </small>
                                                      </a>
                                                    );
                                                  }
                                                })}
                                            </div>
                                          )}
                                        </div>
                                      </div>

                                      <div className="col px-1 d-flex align-items-center">
                                        <div className="dropdown w-100">
                                          <button
                                            className="btn btn-block border px-1 text-left"
                                            type="button"
                                            id="dropdownMenuButton"
                                            {...(intervention.isEdit && {
                                              "data-toggle": "dropdown",
                                            })}
                                            aria-expanded="false"
                                            style={{
                                              height: "38px",
                                              padding: "0 12px",
                                            }}
                                          >
                                            <div className="d-flex align-items-center justify-content-between w-100">
                                              <small className="f-11">
                                                {intervention.provider
                                                  ? `${intervention.provider?.lastName} ${intervention.provider?.firstName}`
                                                  : "Select Provider"}
                                              </small>
                                              <BiChevronDown
                                                size={ICON_SIZE}
                                                className="navbar-btn-icon"
                                              />
                                            </div>
                                          </button>
                                          {intervention.isEdit && (
                                            <div className="dropdown-menu small border-0 shadow">
                                              <p className="pl-4 mb-0 pb-2">
                                                <small className="small-text">
                                                  {providers.length > 0
                                                    ? "Choose one provider only:"
                                                    : "Choose position first"}
                                                </small>
                                              </p>
                                              {providers
                                                .sort((a, b) =>
                                                  a.lastName.localeCompare(
                                                    b.lastName
                                                  )
                                                )
                                                .map((provider, key) => (
                                                  <a
                                                    key={key}
                                                    className="dropdown-item ml-4 py-2"
                                                    href="#"
                                                    onClick={(e) => {
                                                      e.preventDefault();
                                                      handleProvider(
                                                        provider,
                                                        intervIndex,
                                                        careheading.id,
                                                        item.id
                                                      );
                                                    }}
                                                  >
                                                    <small className="small-text">
                                                      {provider?.lastName},{" "}
                                                      {provider?.firstName} (
                                                      {
                                                        provider.position
                                                          .abbreviation
                                                      }
                                                      )
                                                    </small>
                                                  </a>
                                                ))}
                                            </div>
                                          )}
                                        </div>
                                      </div>

                                      {/* Review Date */}
                                      <div className="col-md-2 p-0 my-0 d-flex align-items-center">
                                        <div className="form-group row m-0 w-100">
                                          <label className="col-md-4 pr-3 col-form-label text-right p-0 d-flex align-items-center justify-content-end">
                                            <small className="f-8">
                                              Review <br /> by:
                                            </small>
                                          </label>
                                          <div className="col-md-8 pl-2 p-0 d-flex align-items-center">
                                            <div
                                              style={{
                                                height: "38px",
                                                width: "100%",
                                              }}
                                            >
                                              <InputDatePicker
                                                date={
                                                  intervention.review_date || ""
                                                }
                                                setDate={
                                                  intervention.isEdit
                                                    ? handleSetReviewDate
                                                    : null
                                                }
                                                intervention={intervIndex}
                                                heading={careheading.id}
                                                issueId={item.id}
                                                readOnly={!intervention.isEdit}
                                                height="38px"
                                                style={{ width: "100%" }}
                                                maxDate={new Date()}
                                              />
                                            </div>
                                          </div>
                                        </div>
                                      </div>

                                      {/* Comment */}
                                      <div className="col-md-2 px-0 pl-2 d-flex align-items-center">
                                        <input
                                          type="text"
                                          placeholder="Comments"
                                          value={intervention.comment || ""}
                                          onChange={(e) =>
                                            handleInterventionComment(
                                              e.target.value,
                                              careheading.id,
                                              item.id,
                                              intervIndex
                                            )
                                          }
                                          readOnly={!intervention.isEdit}
                                          className="form-control border f-11 text-center outline-none bg-white"
                                          style={{
                                            height: "38px",
                                            padding: "0 12px",
                                          }}
                                        />
                                      </div>

                                      {/* Actions */}
                                      <div className="col-md-1 border-1 d-flex align-items-center justify-content-center">
                                        <small
                                          className={`cursor-pointer ${
                                            !intervention.isEdit
                                              ? "d-inline-block"
                                              : "d-none"
                                          }`}
                                          onClick={() =>
                                            handleInterventionEdit(
                                              careheading.id,
                                              item.id,
                                              intervIndex,
                                              true
                                            )
                                          }
                                        >
                                          <FiEdit
                                            className="text_primary"
                                            size={ICON_SIZE}
                                          />
                                        </small>
                                        <small
                                          className={`cursor-pointer ${
                                            intervention.isEdit
                                              ? "d-inline-block"
                                              : "d-none"
                                          }`}
                                          onClick={() =>
                                            openAcceptChangesModal(() =>
                                              handleInterventionEdit(
                                                careheading.id,
                                                item.id,
                                                intervIndex,
                                                false
                                              )
                                            )
                                          }
                                          title="Accept all changes"
                                        >
                                          <FiSave
                                            className="text_primary"
                                            size={ICON_SIZE}
                                          />
                                        </small>
                                        <small
                                          className={`cursor-pointer ml-2 ${
                                            intervention.isEdit
                                              ? "d-inline-block"
                                              : "d-none"
                                          }`}
                                          onClick={() =>
                                            openClearRowModal(() =>
                                              handleClearIntervention(
                                                careheading.id,
                                                item.id,
                                                intervIndex
                                              )
                                            )
                                          }
                                          title="Erase all scores and comments"
                                        >
                                          <FiTrash2
                                            className="text_danger"
                                            size={ICON_SIZE}
                                          />
                                        </small>
                                      </div>
                                    </div>
                                  )
                                )}

                                {/* Add another intervention button */}
                                <div className="row mx-0 mt-2">
                                  <div className="col-md-3 pl-5">
                                    <button
                                      onClick={() =>
                                        handleAddIntervention(
                                          careheading.id,
                                          item.id
                                        )
                                      }
                                      className="btn pl-1"
                                    >
                                      <div className="text_primary">
                                        <b>+</b>{" "}
                                        <small className="f-11">
                                          Another intervention
                                        </small>
                                      </div>
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )
                        )}

                        {/* Add another functional issue button */}
                        <button
                          onClick={() =>
                            handleAddFunctionalIssue(careheading.id)
                          }
                          className="btn pl-1"
                        >
                          <div className="mt-0 pt-0 text_primary">
                            <b>+</b>{" "}
                            <small className="f-12">
                              Add another Functional Issue
                            </small>
                          </div>
                        </button>
                      </AccordionItemPanel>
                    </AccordionItem>
                  </div>
                ))}
              </Accordion>
            </div>
            )}
          </div>
        </div>
      </div>

      <FooterButton
        child1={<Item1 onClear={handleClearAction} />}
        child2={
          <Item2
            props={props}
            date={carePlanDate || finalDate}
            handleSaveForLater={handleSaveForLater}
            handleSign={handleSign}
            carePlan={carePlan}
            type={carePlanType}
            meeting={carePlanMeeting}
            meetingTypeId={carePlanMeetingTypeId}
            attendees={carePlanAttendees}
            onAllowNavigation={() => setAllowNavigation(true)}
            carePlanId={finalCarePlanId}
            editReason={requiresEditReason ? editReason : undefined}
            carePlanStatus={carePlanStatus}
            carePlanVersion={carePlanVersion}
            fromTeamScores={fromTeamScores}
            teamScoreFromDate={teamScoreFromDate}
            teamScoreToDate={teamScoreToDate}
          />
        }
      />

      {/* Clear Button Confirmation Modal */}
      <div
        className={`modal fade ${showClearModal ? "show d-block" : ""}`}
        tabIndex="-1"
        style={{
          backgroundColor: showClearModal ? "rgba(0,0,0,0.5)" : "transparent",
        }}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header border-0 d-flex justify-content-center">
              <div className="text-center">
                <FaExclamationTriangle
                  size={ICON_SIZE}
                  className="text-warning mb-2"
                />
                <h5 className="modal-title text_primary font-weight-bold">
                  Warning
                </h5>
              </div>
            </div>
            <div className="modal-body text-center px-4">
              <p className="text_default fs-13">
                Are you sure you want to clear this screen?
              </p>
              <p className="text_default fs-13">
                <b>Note:</b> All care plan data will be lost
              </p>
            </div>
            <div className="modal-footer border-0 d-flex justify-content-between px-4 pb-4">
              <button
                type="button"
                className="btn btn_outline_danger px-4"
                onClick={handleCancelClear}
              >
                <small>GO BACK</small>
              </button>
              <button
                type="button"
                className="btn btn-outline-primary px-4"
                onClick={handleConfirmClear}
              >
                <small>YES</small>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Accept Changes Confirmation Modal */}
      <div
        className={`modal fade ${showAcceptChangesModal ? "show d-block" : ""}`}
        tabIndex="-1"
        style={{
          backgroundColor: showAcceptChangesModal
            ? "rgba(0,0,0,0.5)"
            : "transparent",
        }}
      >
        <div
          className="modal-dialog modal-dialog-centered"
          style={{ width: "395px", maxWidth: "395px" }}
        >
          <div
            className="modal-content"
            style={{
              height: "207px",
              padding: "16px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div className="modal-header border-0 d-flex justify-content-end mt-3 p-0">
              <button
                type="button"
                className="close"
                onClick={handleCancelAcceptChanges}
                aria-label="Close"
              >
                <span
                  className="text_primary"
                  style={{ fontSize: 40, fontWeight: "600" }}
                  aria-hidden="true"
                >
                  &times;
                </span>
              </button>
            </div>
            <div
              className="modal-body text-center p-0"
              style={{
                flex: "1 1 auto",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <p className="text_default fs-13 mb-0">Accept all changes?</p>
            </div>
            <div className="modal-footer border-0 d-flex justify-content-between p-0">
              <button
                type="button"
                className="btn btn_outline_danger px-4"
                onClick={handleCancelAcceptChanges}
              >
                <small>GO BACK</small>
              </button>
              <button
                type="button"
                className="btn btn-outline-primary px-4"
                onClick={handleConfirmAcceptChanges}
              >
                <small>YES</small>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Clear Row Confirmation Modal */}
      <div
        className={`modal fade ${showClearRowModal ? "show d-block" : ""}`}
        tabIndex="-1"
        style={{
          backgroundColor: showClearRowModal
            ? "rgba(0,0,0,0.5)"
            : "transparent",
        }}
      >
        <div
          className="modal-dialog modal-dialog-centered"
          style={{ width: "395px", maxWidth: "395px" }}
        >
          <div
            className="modal-content"
            style={{
              height: "207px",
              padding: "16px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              className="modal-header border-0 mt-3 p-0"
              style={{ position: "relative" }}
            >
              <h5 className="modal-title text_primary font-weight-bold w-100 text-center m-0">
                Warning
              </h5>
              <button
                type="button"
                className="close"
                onClick={handleCancelClearRow}
                aria-label="Close"
                style={{ position: "absolute", right: 0, top: 0 }}
              >
                <span
                  className="text_primary"
                  style={{ fontSize: 40, fontWeight: "600" }}
                  aria-hidden="true"
                >
                  &times;
                </span>
              </button>
            </div>
            <div
              className="modal-body text-center p-0"
              style={{
                flex: "1 1 auto",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <p className="text_default fs-13 mb-1">
                Are you sure you want to clear this row?
              </p>
              <p className="text_default fs-13 mb-0">
                <b>NOTE:</b> All scores and comments will be lost.
              </p>
            </div>
            <div className="modal-footer border-0 d-flex justify-content-between p-0">
              <button
                type="button"
                className="btn btn_outline_danger px-4"
                onClick={handleCancelClearRow}
              >
                <small>GO BACK</small>
              </button>
              <button
                type="button"
                className="btn btn-outline-primary px-4"
                onClick={handleConfirmClearRow}
              >
                <small>YES</small>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Confirmation Modal */}
      <div
        className={`modal fade ${showNavigationModal ? "show d-block" : ""}`}
        tabIndex="-1"
        style={{
          backgroundColor: showNavigationModal
            ? "rgba(0,0,0,0.5)"
            : "transparent",
        }}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header border-0 d-flex justify-content-center">
              <div className="text-center">
                <FaExclamationTriangle
                  size={ICON_SIZE}
                  className="text-warning mb-2"
                />
                <h5 className="modal-title text_primary font-weight-bold">
                  Warning
                </h5>
              </div>
            </div>
            <div className="modal-body text-center px-4">
              <p className="text_default fs-13">
                Are you sure you want to leave this screen?
              </p>
              <p className="text_default fs-13">
                <b>Note:</b> All data will be lost
              </p>
            </div>
            <div className="modal-footer border-0 d-flex justify-content-between px-4 pb-4">
              <button
                type="button"
                className="btn btn_outline_danger px-4"
                onClick={handleCancelNavigation}
              >
                <small>GO BACK</small>
              </button>
              <button
                type="button"
                className="btn btn-outline-primary px-4"
                onClick={handleConfirmNavigation}
              >
                <small>YES</small>
              </button>
            </div>
          </div>
        </div>
      </div>

      <LatestFunctionalScoresOverlay
        isOpen={showLatestScoresOverlay}
        onClose={() => setShowLatestScoresOverlay(false)}
        headings={functionalScoresWithTeamScores?.data || []}
        startLabel={moment(functionalScoresOverlayRange.startDate).format(
          "D MMM YYYY",
        )}
        endLabel={moment(functionalScoresOverlayRange.endDate).format(
          "D MMM YYYY",
        )}
        isLoading={isLatestScoresLoading}
        error={latestScoresError}
      />

      <TeamScoresOverlay
        isOpen={showTeamScoreOverlay}
        onClose={() => setShowTeamScoreOverlay(false)}
        startLabel={teamScoreRange.start.format("DD MMM YYYY")}
        endLabel={teamScoreRange.end.format("DD MMM YYYY")}
        headings={teamScoreData?.data || []}
        isLoading={isTeamScoreLoading}
        error={teamScoreError}
      />
    </>
  );
};

export default ReviewCarePlan;
